import { useState, useEffect, Component } from 'react'
import type { ReactNode, ErrorInfo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { toast } from 'sonner'
import {
  ArrowLeft, Play, RefreshCw, TrendingUp, TrendingDown, Minus,
  ExternalLink, Bot, Copy, Check, Shield, AlertCircle, Link2Off, X, PauseCircle, ChevronDown,
} from 'lucide-react'
import { formatDistanceToNow, format } from 'date-fns'
import { nl } from 'date-fns/locale'
import { useQueryClient } from '@tanstack/react-query'
import {
  ComposedChart, Line, Scatter, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
  BarChart, Bar, Cell,
} from 'recharts'
import {
  useDossier, useDossierSnapshots, useDossierScans, useDossierSecurityScans,
  useDossierScoreHistory, useDossierIndexReport, useDossierPositionBuckets, useGscStatus,
  useDossierCompetitorComparison, useScanCompetitors, useUpdateDossier, useDossierGscTrends,
  useContentAudits, useTriggerContentAudit, useTriggerContentAuditBulk, useTopicalGaps, useGscPages, useContentGaps, useScanSummary,
  useScanSchedules, useUpdateScanSchedules, useDossierSerpAio,
  useCitationPrompts, useGenerateCitationPrompts, useCreateCitationPrompt, useUpdateCitationPrompt, useDeleteCitationPrompt,
  useWordPressSite, useWordPressScanResults, useSaveWordPressSite, useDeleteWordPressSite,
  useTestWordPressConnection, useTriggerWordPressScan,
} from '@/hooks/useApi'
import { dossiersApi, seoApi, securityApi } from '@/api/client'
import type { GscSnapshotData, SeoScan, SeoFinding, SecurityScan, SecurityFinding, Dossier, IndexReport, PositionBuckets, GscTrends, GscTrendKpi, ContentAudit, TopicalGap, ScanTypeSummary, ScanItem, ScanSchedule, WordPressPlugin, WordPressContentItem, ClaudeCodeInstruction, VectorContentGap, SerpAioResult, CitationPrompt } from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Select } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { generateSeoPrompt } from '@/lib/seoPrompt'
import { generateContentAuditPrompt } from '@/lib/contentAuditPrompt'
import { InstructionsPanel } from '@/components/InstructionsPanel'
import { GscTools } from '@/components/dossier/GscTools'

// ── Score helpers ─────────────────────────────────────────────────────────────

const SCAN_TYPE_LABELS: Record<string, string> = {
  onpage: 'On-page',
  tech_audit: 'Technisch',
  site_crawl: 'Site crawl',
  gsc_index: 'GSC-indexatie',
}

// R10: indexability buckets — Dutch label + badge colour.
const INDEX_BUCKETS: { key: string; label: string; cls: string }[] = [
  { key: 'indexable', label: 'Indexeerbaar', cls: 'border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300' },
  { key: 'google_not_indexed', label: 'Niet geïndexeerd', cls: 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300' },
  { key: 'noindex', label: 'Noindex', cls: 'border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300' },
  { key: 'canonicalized_away', label: 'Canonical elders', cls: 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300' },
  { key: 'error', label: 'Fout (4xx/5xx)', cls: 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300' },
]
const INDEX_BUCKET_LABEL: Record<string, string> = Object.fromEntries(
  INDEX_BUCKETS.map((b) => [b.key, b.label]),
)
const INDEX_BUCKET_CLS: Record<string, string> = Object.fromEntries(
  INDEX_BUCKETS.map((b) => [b.key, b.cls]),
)

// R11: position bands — order + colour (green = good rank, red = poor).
const POSITION_BANDS: { key: string; label: string; color: string }[] = [
  { key: '1-3', label: 'Positie 1–3', color: '#22c55e' },
  { key: '4-10', label: 'Positie 4–10', color: '#84cc16' },
  { key: '11-20', label: 'Positie 11–20', color: '#f59e0b' },
  { key: '21+', label: 'Positie 21+', color: '#ef4444' },
]

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

// R22: the 7 SEO pillars for the competitor comparison.
// Opt-in tabs: present for every dossier, configured for few. Counts are not
// shown on the trigger — a "(0)" reads as a broken feature rather than an
// unused one.
const MORE_TABS = [
  { key: 'concurrenten', label: 'Concurrenten' },
  { key: 'wordpress', label: 'WordPress' },
] as const

// The SBOM/CVE view is a page of its own (/inventory/:id) and already shows
// components, per-component CVEs and the last SBOM date. Linking to it beats
// rebuilding it as a tab — a second copy is what this cleanup exists to
// remove. Only shown for dossiers that actually push an inventory.
const INVENTORY_PLATFORMS = ['python', 'node']

const SEO_PILLARS: { key: string; label: string }[] = [
  { key: 'indexability', label: 'Indexeerbaarheid' },
  { key: 'content', label: 'Content' },
  { key: 'technical', label: 'Technisch' },
  { key: 'structured', label: 'Gestructureerde data' },
  { key: 'links', label: 'Links' },
  { key: 'mobile', label: 'Mobiel' },
  { key: 'security', label: 'Beveiliging' },
]

// Build a copy-pasteable agent prompt from the indexability report (R10).
function buildIndexPrompt(d: Dossier, r: IndexReport): string {
  const L: string[] = [
    `# Indexeerbaarheid fixen — ${d.name} (${d.url})`,
    '',
    'Onderstaande indexstatus per pagina komt uit de laatste site-crawl + GSC URL-inspectie. ' +
      'Fix de niet-intentionele gevallen zodat de pagina (weer) indexeerbaar is. Verifieer na elke ' +
      'wijziging dat de pagina correct rendert en niets anders breekt.',
    '',
    'Buckets: ' + INDEX_BUCKETS.map((b) => `${b.label} ${r.buckets[b.key] ?? 0}`).join(' · '),
    '',
  ]
  const urls = (key: string) => r.pages.filter((p) => p.bucket === key).map((p) => p.url)
  let n = 1
  const section = (title: string, items: string[]) => {
    if (!items.length) return
    L.push(`## ${n++}. ${title}`)
    items.slice(0, 50).forEach((u) => L.push(`- ${u}`))
    if (items.length > 50) L.push(`- … en ${items.length - 50} meer`)
    L.push('')
  }
  section("Fout-pagina's (4xx/5xx) — herstel of redirect naar een geldige pagina", urls('error'))
  section('Google indexeert deze niet — onderzoek de reden (dunne content, duplicatie, crawl-budget)', urls('google_not_indexed'))
  section('Canonical wijst naar een andere pagina (alternate) — bevestig of corrigeer de canonical', urls('canonicalized_away'))
  section('Noindex — verwijder de noindex indien onbedoeld', urls('noindex'))
  section(
    `Redirects (${r.redirected}) — controleer of het één hop is (geen keten)`,
    r.redirected_urls.map((x) => `${x.url} → ${x.to}`),
  )
  section(`Robots-geblokkeerd (${r.robots_blocked}) — bevestig of dit bewust is`, r.robots_blocked_urls)
  section(
    'Conflict: krijgt GSC-verkeer maar is niet-indexeerbaar — prioriteit',
    r.conflicts.map((c) => `${c.url} (${INDEX_BUCKET_LABEL[c.bucket] ?? c.bucket})`),
  )
  if (n === 1) L.push('Geen indexeerbaarheids-problemen gevonden. 🎉')
  return L.join('\n')
}

// Build a copy-pasteable agent prompt from the GSC position/cannibalization data (R11).
function buildGscPrompt(d: Dossier, pb: PositionBuckets): string {
  const L: string[] = [
    `# GSC-acties — ${d.name} (${d.url})`,
    '',
    '## Positieverdeling (aantal zoektermen per rang-band)',
    ...POSITION_BANDS.map((b) => `- ${b.label}: ${pb.buckets[b.key]?.keywords ?? 0} zoektermen`),
    '',
  ]
  if (pb.cannibalization.length) {
    L.push(`## Keyword-kannibalisatie (${pb.cannibalization.length}) — consolideer of differentieer`)
    L.push(
      "Per zoekterm concurreren meerdere pagina's in de top 20. Kies één doelpagina per term, " +
        'en laat de andere ernaar linken / canonical-en of voeg ze samen. Verifieer daarna de rankings.',
    )
    L.push('')
    pb.cannibalization.slice(0, 15).forEach((c) => {
      L.push(`- "${c.query}": ${c.pages.map((p) => `${p.page} (#${p.position})`).join(', ')}`)
    })
  } else {
    L.push('Geen keyword-kannibalisatie gedetecteerd.')
  }
  return L.join('\n')
}

function scoreColor(score: number | null | undefined): string {
  if (score === null || score === undefined) return 'text-muted-foreground'
  if (score >= 75) return 'text-green-600 dark:text-green-300'
  if (score >= 50) return 'text-orange-500'
  return 'text-red-500'
}

function getLatestScoreByType(scans: SeoScan[]): Record<string, number | null> {
  const result: Record<string, number | null> = { onpage: null, tech_audit: null, site_crawl: null }
  for (const type of Object.keys(result)) {
    const latest = scans.find(s => s.scan_type === type && s.status === 'completed' && s.score !== null)
    if (latest) result[type] = latest.score
  }
  return result
}

function getAvgScore(byType: Record<string, number | null>): number | null {
  // Clamp defensively: an out-of-range score (e.g. a corrupted LLM
  // overall_score) must never drag the average outside 0-100 — this is the
  // client-side half of the fix for the KPI card showing e.g. "715".
  const scores = Object.values(byType)
    .filter((s): s is number => s !== null)
    .map(s => Math.max(0, Math.min(100, s)))
  if (!scores.length) return null
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
}

// ── Trend chip ────────────────────────────────────────────────────────────────

function TrendChip({ current, previous }: { current: number; previous: number | undefined }) {
  if (previous === undefined) return null
  const diff = current - previous
  if (Math.abs(diff) < 0.5) return (
    <span className="text-xs text-muted-foreground flex items-center gap-0.5">
      <Minus className="h-3 w-3" /> gelijk
    </span>
  )
  const up = diff > 0
  return (
    <span className={cn('text-xs flex items-center gap-0.5', up ? 'text-green-600 dark:text-green-300' : 'text-red-500')}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {up ? '+' : ''}{Math.round(diff)} vs vorige maand
    </span>
  )
}

// ── Claude Code prompt card ───────────────────────────────────────────────────

function ClaudeCodePromptCard({ instruction }: { instruction: ClaudeCodeInstruction }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(instruction.prompt)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  const priorityCls = instruction.priority === 'hoog'
    ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'
    : instruction.priority === 'gemiddeld'
    ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800'
    : 'bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700'
  return (
    <div className={cn('border rounded p-3 text-sm', priorityCls)}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-foreground">{instruction.title}</p>
          <p className="mt-1 text-muted-foreground font-mono text-xs leading-relaxed break-words">{instruction.prompt}</p>
        </div>
        <button
          onClick={handleCopy}
          className="shrink-0 p-1 rounded hover:bg-black/5 text-muted-foreground hover:text-foreground transition-colors"
          title="Kopieer prompt"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-300" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  )
}

// ── GSC Trend components ───────────────────────────────────────────────────────

function KpiDelta({ label, kpi, unit = '', invert = false }: {
  label: string
  kpi: GscTrendKpi
  unit?: string
  invert?: boolean  // true = lower is better (e.g. position)
}) {
  const pct = kpi.delta_pct
  const isPositive = pct !== null && (invert ? pct < 0 : pct > 0)
  const isNegative = pct !== null && (invert ? pct > 0 : pct < 0)
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold tabular-nums">
        {typeof kpi.current === 'number' && unit === '%'
          ? `${kpi.current.toLocaleString('nl-BE', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%`
          : kpi.current.toLocaleString('nl')
        }
      </p>
      {pct !== null ? (
        <p className={cn(
          'text-xs flex items-center gap-0.5',
          isPositive ? 'text-green-600 dark:text-green-300' : isNegative ? 'text-red-500' : 'text-muted-foreground',
        )}>
          {isPositive ? <TrendingUp className="h-3 w-3" /> : isNegative ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
          {pct > 0 ? '+' : ''}{pct}% vs vorige 28d
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">geen vorig. periode</p>
      )}
    </div>
  )
}

function GscTrendsSection({ trends }: { trends: GscTrends }) {
  if (!trends.has_gsc || !trends.kpis) return null

  const { kpis, timeline = [], bucket_migration } = trends

  const timelineData = timeline.map(r => ({
    ...r,
    label: format(new Date(r.week_start), 'd MMM', { locale: nl }),
  }))

  const bucketData = POSITION_BANDS.map(b => ({
    label: b.label,
    huidig: bucket_migration?.current[b.key] ?? 0,
    vorig: bucket_migration?.previous[b.key] ?? 0,
    color: b.color,
  }))

  const hasTimeline = timelineData.some(r => r.clicks > 0 || r.impressions > 0)
  const hasBuckets = bucketData.some(r => r.huidig > 0 || r.vorig > 0)

  return (
    <Card>
      <CardHeader>
        <CardTitle>GSC prestatie-trends</CardTitle>
        <CardDescription>
          Huidige 28 dagen vs vorige 28 dagen · meest betrouwbare maatstaf voor impact van wijzigingen
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* KPI delta cards */}
        <div className="grid grid-cols-3 gap-4 p-4 rounded-lg bg-muted/30 border">
          <KpiDelta label="Clicks (28d)" kpi={kpis.clicks} />
          <KpiDelta label="Impressies (28d)" kpi={kpis.impressions} />
          <KpiDelta label="CTR (28d)" kpi={kpis.ctr} unit="%" />
        </div>

        {/* 12-week timeline */}
        {hasTimeline && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">12-weken tijdlijn</p>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={timelineData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} interval="preserveStartEnd" />
                <YAxis
                  yAxisId="l"
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  label={{ value: 'Clicks', angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 10 } }}
                />
                <YAxis
                  yAxisId="r"
                  orientation="right"
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  label={{ value: 'Impressies', angle: 90, position: 'insideRight', offset: 10, style: { fontSize: 10 } }}
                />
                <Tooltip
                  content={<ChartTooltip />}
                  formatter={(v: number, name: string) => [v.toLocaleString('nl'), name]}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="l" dataKey="clicks" name="Clicks" fill="#f97316" radius={[3, 3, 0, 0]} maxBarSize={40} />
                <Line
                  yAxisId="r"
                  type="monotone"
                  dataKey="impressions"
                  name="Impressies"
                  stroke="#94a3b8"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Bucket migration */}
        {hasBuckets && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">
              Positie-bucket migratie — hoeveel zoektermen per rang-band (huidig vs vorig 28d)
            </p>
            <p className="text-xs text-muted-foreground mb-2">
              Groeiende groene/lime balken = meer queries in de top 10 → structurele verbetering
            </p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={bucketData}
                margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                barGap={2}
                barCategoryGap="25%"
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip
                  formatter={(v: number, name: string) => [`${v} zoektermen`, name]}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="vorig" name="Vorige 28d" fill="#94a3b8" radius={[3, 3, 0, 0]} maxBarSize={24} />
                <Bar dataKey="huidig" name="Huidige 28d" radius={[3, 3, 0, 0]} maxBarSize={24}>
                  {bucketData.map(b => (
                    <Cell key={b.label} fill={b.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ── Scan status ───────────────────────────────────────────────────────────────

function ScanStatusBadge({ status }: { status: string }) {
  const classes: Record<string, string> = {
    pending: 'bg-yellow-500 text-white',
    running: 'bg-blue-500 text-white',
    completed: 'bg-green-600 text-white',
    failed: 'bg-red-600 text-white',
    failed_fetch: 'bg-amber-500 text-white',
    blocked: 'bg-orange-500 text-white',
    skipped_locked: 'bg-gray-400 text-white',
    skipped_cached: 'bg-gray-400 text-white',
  }
  const labels: Record<string, string> = {
    pending: 'Wacht', running: 'Loopt', completed: 'Voltooid', failed: 'Mislukt',
    failed_fetch: 'Pagina niet opgehaald',
    blocked: 'Geblokkeerd',
    skipped_locked: 'Wacht op slot',
    skipped_cached: 'Overgeslagen (24u cache)',
  }
  return (
    <Badge className={cn('shrink-0', classes[status] ?? 'bg-gray-500 text-white')}>
      {labels[status] ?? status}
    </Badge>
  )
}

// ── Chart ─────────────────────────────────────────────────────────────────────

function buildChartData(
  snapshots: GscSnapshotData[],
  scoreHistory: { date: string; score: number; algo_version?: number }[],
) {
  // SEO score points come from the permanent score-history (survives scan deletion)
  const scoreByDate: Record<string, number> = {}
  for (const r of scoreHistory) scoreByDate[r.date] = r.score

  // Union of all dates (snapshots + score records) so score points show even
  // on days without a GSC snapshot
  const allDates = new Set<string>([
    ...snapshots.map(s => s.snapshot_date),
    ...scoreHistory.map(r => r.date),
  ])
  const snapByDate: Record<string, GscSnapshotData> = {}
  for (const s of snapshots) snapByDate[s.snapshot_date] = s

  return [...allDates].sort().map(date => ({
    date,
    dateLabel: format(new Date(date), 'd MMM', { locale: nl }),
    clicks: snapByDate[date]?.total_clicks ?? null,
    impressions: snapByDate[date]?.total_impressions ?? null,
    // Position only exists on snapshot days; 0 means "no data" so treat it as null.
    position: snapByDate[date]?.avg_position || null,
    seoScore: scoreByDate[date] ?? null,
  }))
}

function ChartTooltip({ active, payload, label }: {
  active?: boolean
  payload?: { name: string; value: number | null; color: string }[]
  label?: string
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border bg-background p-3 shadow-lg text-xs space-y-1">
      <p className="font-medium">{label}</p>
      {payload.map(p => p.value !== null && (
        <p key={p.name} style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString('nl') : p.value}
        </p>
      ))}
    </div>
  )
}

// ── Claude Code instructies ───────────────────────────────────────────────────
// generateSeoPrompt moved to @/lib/seoPrompt (S23 Task 5) so it's importable
// from a Vitest snapshot test without pulling in this whole page component.

function generateSecurityPrompt(
  dossier: Dossier,
  securityScans: SecurityScan[],
  securityFindings: SecurityFinding[],
): string {
  const lines: string[] = []
  const latestScan = securityScans.find(s => s.status === 'completed')

  lines.push(`Los de security problemen op voor ${dossier.name} (${dossier.url}).`)
  if (dossier.client_name) lines.push(`Klant: ${dossier.client_name}`)
  lines.push('')

  if (latestScan) {
    lines.push(`Scan type: ${latestScan.scan_type} · ${latestScan.total_findings} bevindingen`)
    lines.push(`Critical: ${latestScan.critical_count} · High: ${latestScan.high_count} · Medium: ${latestScan.medium_count} · Low: ${latestScan.low_count}`)
    lines.push('')
  }

  lines.push(
    `Fix alle problemen hieronder in volgorde van prioriteit. ` +
    `Zoek zelf de relevante bestanden of configuraties. ` +
    `Verifieer na elke aanpassing dat de applicatie nog correct werkt.`
  )
  lines.push('')

  const critical = securityFindings.filter(f => f.severity === 'critical')
  const high = securityFindings.filter(f => f.severity === 'high')
  const medium = securityFindings.filter(f => f.severity === 'medium')

  let taskNum = 1

  const addSecSection = (items: SecurityFinding[], label: string, emoji: string) => {
    if (!items.length) return
    lines.push(`## ${emoji} ${label} (${items.length})`)
    lines.push('')
    for (const f of items) {
      lines.push(`${taskNum++}. **${f.name}**`)
      if (f.url) lines.push(`   URL: ${f.url}`)
      if (f.description) lines.push(`   ${f.description.slice(0, 300)}`)
      if (f.evidence) lines.push(`   Bewijs: \`${f.evidence.slice(0, 200)}\``)
      if (f.solution) lines.push(`   → Oplossing: ${f.solution.slice(0, 400)}`)
      if (f.reference) lines.push(`   Referentie: ${f.reference.slice(0, 200)}`)
      lines.push('')
    }
  }

  addSecSection(critical, 'Kritieke kwetsbaarheden', '🔴')
  addSecSection(high, 'Hoge risico\'s', '🟠')
  addSecSection(medium, 'Gemiddelde risico\'s', '🟡')

  lines.push('---')
  lines.push(`Na alle fixes: start een nieuwe security scan via de Security-tab om de verbeteringen te verifiëren.`)

  return lines.join('\n')
}

// ── Security scan row met uitklapbare bevindingen ────────────────────────────

const SEVERITY_COLORS_SEC: Record<string, string> = {
  critical: 'text-red-600 dark:text-red-300',
  high: 'text-orange-500',
  medium: 'text-yellow-600 dark:text-yellow-300',
  low: 'text-blue-500',
  info: 'text-muted-foreground',
}

interface SecurityScanRowProps {
  scan: SecurityScan
  dossierId: number
}

function SecurityScanRow({ scan, dossierId }: SecurityScanRowProps) {
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [findings, setFindings] = useState<SecurityFinding[]>([])
  const [loadingFindings, setLoadingFindings] = useState(false)
  const [togglingId, setTogglingId] = useState<number | null>(null)

  const handleExpand = async () => {
    if (expanded) { setExpanded(false); return }
    setExpanded(true)
    if (findings.length === 0 && scan.status === 'completed') {
      setLoadingFindings(true)
      try {
        const data = await securityApi.getScanFindings(scan.id)
        setFindings(data)
      } catch { /* ignore */ }
      finally { setLoadingFindings(false) }
    }
  }

  const handleToggleFP = async (finding: SecurityFinding) => {
    setTogglingId(finding.id)
    try {
      const updated = await securityApi.markFalsePositive(finding.id, !finding.is_false_positive)
      setFindings(prev => prev.map(f => f.id === finding.id ? updated : f))
      // Refresh security scan list so counts update
      queryClient.invalidateQueries({ queryKey: ['dossiers', dossierId, 'security-scans'] })
      queryClient.invalidateQueries({ queryKey: ['dossiers'] })
    } catch { /* ignore */ }
    finally { setTogglingId(null) }
  }

  const realFindings = findings.filter(f => !f.is_false_positive)
  const fpFindings = findings.filter(f => f.is_false_positive)

  return (
    <div className="border-b last:border-0">
      {/* Scan header row */}
      <button
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted/30 transition-colors text-left"
        onClick={handleExpand}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={cn('shrink-0',
              scan.status === 'completed' ? 'bg-green-600 text-white' :
              scan.status === 'running' ? 'bg-blue-500 text-white' :
              scan.status === 'failed' ? 'bg-red-600 text-white' :
              scan.status === 'blocked' ? 'bg-orange-500 text-white' :
              'bg-yellow-500 text-white'
            )}>
              {scan.status === 'completed' ? 'Voltooid' : scan.status === 'running' ? 'Loopt' : scan.status === 'failed' ? 'Mislukt' : scan.status === 'blocked' ? 'Geblokkeerd' : 'Wacht'}
            </Badge>
            <span className="text-xs font-medium">{scan.scan_type}</span>
            {scan.total_findings > 0 && (
              <span className="text-xs text-muted-foreground">{scan.total_findings} bevindingen</span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(scan.created_at), { addSuffix: true, locale: nl })}
            </span>
            {scan.critical_count > 0 && <span className="text-xs text-red-600 dark:text-red-300">{scan.critical_count} krit.</span>}
            {scan.high_count > 0 && <span className="text-xs text-orange-500">{scan.high_count} hoog</span>}
            {scan.medium_count > 0 && <span className="text-xs text-yellow-600 dark:text-yellow-300">{scan.medium_count} medium</span>}
          </div>
          {(scan.status === 'pending' || scan.status === 'running') && (
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
              {scan.progress_percent !== null
                ? <div className="h-full bg-primary transition-all" style={{ width: `${scan.progress_percent}%` }} />
                : <div className="h-full w-full bg-primary/50 animate-pulse" />
              }
            </div>
          )}
          {scan.error_message && (
            <p className="mt-0.5 text-xs text-red-600 dark:text-red-300 font-mono break-all">{scan.error_message}</p>
          )}
        </div>
        {scan.status === 'completed' && scan.total_findings > 0 && (
          <span className="text-xs text-muted-foreground shrink-0">
            {expanded ? '▲ sluiten' : '▼ bevindingen'}
          </span>
        )}
      </button>

      {/* Uitklapbare bevindingen */}
      {expanded && (
        <div className="border-t bg-muted/20 px-4 py-3 space-y-2">
          {loadingFindings ? (
            <div className="flex justify-center py-4"><Spinner size="sm" /></div>
          ) : findings.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-2">Geen bevindingen.</p>
          ) : (
            <>
              {realFindings.length > 0 && (
                <div className="space-y-2">
                  {realFindings.map(f => (
                    <div key={f.id} className="rounded-lg border bg-background p-3 space-y-1.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={cn('text-xs font-bold uppercase', SEVERITY_COLORS_SEC[f.severity] ?? 'text-muted-foreground')}>
                              {f.severity}
                            </span>
                            <span className="text-sm font-medium">{f.name}</span>
                          </div>
                          {f.url && <p className="text-xs text-muted-foreground truncate">{f.url}</p>}
                          {f.description && <p className="text-xs text-muted-foreground mt-0.5">{f.description.slice(0, 200)}</p>}
                          {f.evidence && (
                            <p className="text-xs font-mono bg-muted px-2 py-1 rounded mt-1 break-all">
                              {f.evidence.slice(0, 150)}
                            </p>
                          )}
                          {f.solution && <p className="text-xs text-blue-600 dark:text-blue-300 mt-0.5">→ {f.solution.slice(0, 200)}</p>}
                        </div>
                        <button
                          onClick={() => handleToggleFP(f)}
                          disabled={togglingId === f.id}
                          className="shrink-0 rounded border px-2 py-1 text-xs text-muted-foreground hover:text-destructive hover:border-destructive transition-colors"
                          title="Markeer als false positive"
                        >
                          {togglingId === f.id ? '...' : 'False positive'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {fpFindings.length > 0 && (
                <details className="text-xs text-muted-foreground">
                  <summary className="cursor-pointer hover:text-foreground">
                    {fpFindings.length} false positive{fpFindings.length > 1 ? 's' : ''} (verborgen)
                  </summary>
                  <div className="mt-2 space-y-1 opacity-50">
                    {fpFindings.map(f => (
                      <div key={f.id} className="flex items-center justify-between gap-2 rounded border px-3 py-1.5 line-through">
                        <span className="text-xs">{f.name}</span>
                        <button
                          onClick={() => handleToggleFP(f)}
                          className="text-xs text-primary hover:underline shrink-0"
                        >
                          Herstel
                        </button>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ── Per-page error boundary (shows error details for debugging) ───────────────

class DossierErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { error: null }
  }
  static getDerivedStateFromError(error: Error) { return { error } }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[DossierDetail crash]', error.message, error.stack, info.componentStack)
  }
  render() {
    if (this.state.error) {
      return (
        <div className="p-8 space-y-4">
          <h2 className="text-xl font-bold text-destructive">Dossier kon niet geladen worden</h2>
          <pre className="text-xs bg-muted p-4 rounded overflow-auto whitespace-pre-wrap">
            {this.state.error.message}
            {'\n\n'}
            {this.state.error.stack}
          </pre>
          <button className="rounded bg-primary px-4 py-2 text-primary-foreground text-sm"
            onClick={() => this.setState({ error: null })}>
            Opnieuw proberen
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

// ── Main page ─────────────────────────────────────────────────────────────────

function DossierDetailInner() {
  const { id } = useParams<{ id: string }>()
  const dossierId = parseInt(id ?? '0')
  const [moreOpen, setMoreOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'overzicht' | 'scans' | 'security' | 'snapshots' | 'indexeerbaarheid' | 'concurrenten' | 'content' | 'wordpress' | 'claude'>('overzicht')
  const [secScanType, setSecScanType] = useState('zap_baseline')
  const [startingSecurity, setStartingSecurity] = useState(false)
  const [chartDays, setChartDays] = useState(90)
  const [scanningAll, setScanningAll] = useState(false)
  const [selectedScanTypes, setSelectedScanTypes] = useState<Set<string>>(new Set())
  const [backfilling, setBackfilling] = useState(false)

  // Claude Code instructies state per tab
  const [seoClaudeLoading, setSeoClaudeLoading] = useState(false)
  const [seoFindingsByType, setSeoFindingsByType] = useState<Record<string, SeoFinding[]>>({})
  const [secClaudeLoading, setSecClaudeLoading] = useState(false)
  const [secFindingsForPrompt, setSecFindingsForPrompt] = useState<SecurityFinding[]>([])

  const queryClient = useQueryClient()

  const { data: dossier, isLoading: dossierLoading } = useDossier(dossierId)
  const { data: gscStatus } = useGscStatus()
  const { data: snapshots, isLoading: snapshotsLoading } = useDossierSnapshots(dossierId, chartDays)
  const { data: scans } = useDossierScans(dossierId)
  const { data: scoreHistory } = useDossierScoreHistory(dossierId, chartDays)
  const { data: indexReport } = useDossierIndexReport(dossierId)
  const { data: positionBuckets, isLoading: positionBucketsLoading } = useDossierPositionBuckets(dossierId)
  const { data: gscTrends, isLoading: gscTrendsLoading, isError: gscTrendsError } = useDossierGscTrends(dossierId)
  const { data: competitorComparison } = useDossierCompetitorComparison(dossierId)
  const scanCompetitors = useScanCompetitors()
  const updateDossier = useUpdateDossier()
  const [newCompetitor, setNewCompetitor] = useState('')

  const saveCompetitors = (list: string[]) =>
    updateDossier.mutate({ id: dossierId, data: { competitors: list } }, {
      onError: () => toast.error('Opslaan van concurrenten mislukt'),
    })
  const addCompetitor = () => {
    const u = newCompetitor.trim()
    if (!u || !dossier || dossier.competitors?.includes(u)) {
      setNewCompetitor('')
      return
    }
    saveCompetitors([...(dossier.competitors ?? []), u])
    setNewCompetitor('')
  }
  const removeCompetitor = (u: string) => {
    if (dossier) saveCompetitors((dossier.competitors ?? []).filter((x) => x !== u))
  }
  const { data: securityScans, isLoading: secScansLoading } = useDossierSecurityScans(dossierId)
  const { data: scanSummary, isLoading: scanSummaryLoading } = useScanSummary(dossierId)
  const [expandedScanType, setExpandedScanType] = useState<string | null>(null)
  const { data: scanSchedules } = useScanSchedules(dossierId)
  const updateSchedules = useUpdateScanSchedules(dossierId)
  const [localSchedules, setLocalSchedules] = useState<ScanSchedule[] | null>(null)
  const [schedulesDirty, setSchedulesDirty] = useState(false)
  const { data: contentAudits, isLoading: contentAuditsLoading } = useContentAudits(dossierId)
  const triggerAudit = useTriggerContentAudit(dossierId)
  const triggerAuditBulk = useTriggerContentAuditBulk(dossierId)
  const [showContentAuditPrompt, setShowContentAuditPrompt] = useState(false)
  const { data: topicalGaps } = useTopicalGaps(dossierId)
  const { data: contentGaps } = useContentGaps(dossierId)
  const { data: serpAio } = useDossierSerpAio(dossierId)
  const { data: citationPrompts } = useCitationPrompts(dossierId)
  const generatePrompts = useGenerateCitationPrompts(dossierId)
  const createPrompt = useCreateCitationPrompt(dossierId)
  const updatePrompt = useUpdateCitationPrompt(dossierId)
  const deletePrompt = useDeleteCitationPrompt(dossierId)
  const [newPromptText, setNewPromptText] = useState('')
  const { data: gscPages } = useGscPages(dossierId)
  const [selectedAuditPage, setSelectedAuditPage] = useState('')
  const [expandedAuditId, setExpandedAuditId] = useState<number | null>(null)

  // WordPress integration state
  const { data: wpSite } = useWordPressSite(dossierId)
  const { data: wpScanResults } = useWordPressScanResults(dossierId)
  const saveWpSite = useSaveWordPressSite(dossierId)
  const deleteWpSite = useDeleteWordPressSite(dossierId)
  const testWpConnection = useTestWordPressConnection(dossierId)
  const triggerWpScan = useTriggerWordPressScan(dossierId)
  const [wpForm, setWpForm] = useState({ site_url: '', api_user: '', api_password: '', enabled: true })
  const [wpTestResult, setWpTestResult] = useState<{ ok: boolean; site_info?: Record<string, string>; error?: string } | null>(null)

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['dossiers', dossierId] })
    queryClient.invalidateQueries({ queryKey: ['seo', 'scans'] })
    queryClient.invalidateQueries({ queryKey: ['dossiers', dossierId, 'security-scans'] })
  }

  // Refetch after a delay — worker tasks run async
  const delayedRefetch = (ms = 5000) => setTimeout(() => invalidateAll(), ms)

  const handleScanAll = async () => {
    if (!dossier) return
    setScanningAll(true)
    try {
      await dossiersApi.scanAll(dossier.id)
      toast.success('3 SEO-scans gestart')
      delayedRefetch(3000)
      delayedRefetch(10000)
    } catch (err) {
      toast.error('Scans starten mislukt', { description: err instanceof Error ? err.message : '' })
    } finally {
      setScanningAll(false)
    }
  }

  const handleTriggerSelected = async () => {
    if (!dossier || selectedScanTypes.size === 0) return
    setScanningAll(true)
    const types = [...selectedScanTypes]
    let ok = 0
    let fail = 0
    for (const scan_type of types) {
      try {
        await dossiersApi.triggerScan(dossier.id, scan_type)
        ok++
      } catch {
        fail++
      }
    }
    setScanningAll(false)
    setSelectedScanTypes(new Set())
    if (ok > 0) toast.success(`${ok} scan${ok > 1 ? 's' : ''} gestart`)
    if (fail > 0) toast.error(`${fail} scan${fail > 1 ? 's' : ''} mislukt`)
    delayedRefetch(3000)
    delayedRefetch(10000)
  }

  // GSC-only refresh: re-fetches per-day GSC data up to today and corrects
  // existing snapshots. Does NOT trigger SEO scans (those run from the SEO tab).
  const handleBackfill = async () => {
    if (!dossier) return
    setBackfilling(true)
    try {
      await dossiersApi.backfillGsc(dossier.id, 90)
      toast.success('GSC wordt bijgewerkt', {
        description: 'De per-dag GSC-data (tot vandaag) wordt op de achtergrond opgehaald.',
      })
      delayedRefetch(15000)
      delayedRefetch(30000)
    } catch (err) {
      toast.error('GSC bijwerken mislukt', { description: err instanceof Error ? err.message : '' })
    } finally {
      setBackfilling(false)
    }
  }

  // Fetch what the Claude tab's SEO and security prompts need, once, when that
  // tab is opened. Before the early-return guards below: a hook that sits
  // after them runs on some renders and not others, which is React error #310
  // ("Rendered more hooks than during the previous render") and takes the
  // whole page down.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (activeTab !== 'claude') return
    if (
      dossier && scans?.some(s => s.status === 'completed')
      && Object.keys(seoFindingsByType).length === 0 && !seoClaudeLoading
    ) {
      void loadSeoFindingsForPrompt()
    }
    if (
      dossier && securityScans?.some(s => s.status === 'completed')
      && secFindingsForPrompt.length === 0 && !secClaudeLoading
    ) {
      void loadSecFindingsForPrompt()
    }
  }, [activeTab, dossier, scans, securityScans])

  if (dossierLoading) return <div className="flex justify-center py-16"><Spinner size="lg" /></div>
  if (!dossier) return <p className="text-destructive py-16 text-center">Dossier niet gevonden.</p>

  const latestSnapshot = snapshots?.[snapshots.length - 1]
  // 28-day rolling totals computed from per-day snapshots (consistent metric)
  const sortedSnaps = snapshots ? [...snapshots].sort((a, b) => a.snapshot_date.localeCompare(b.snapshot_date)) : []
  const last28 = sortedSnaps.slice(-28)
  const prev28 = sortedSnaps.slice(-56, -28)
  const sum28 = (key: 'total_clicks' | 'total_impressions') => last28.reduce((acc, s) => acc + s[key], 0)
  const sumPrev28 = (key: 'total_clicks' | 'total_impressions') => prev28.reduce((acc, s) => acc + s[key], 0)
  const clicks28 = sum28('total_clicks')
  const impressions28 = sum28('total_impressions')
  const avgPos28 = last28.length ? last28.reduce((a, s) => a + s.avg_position, 0) / last28.length : null
  const avgPosPrev28 = prev28.length ? prev28.reduce((a, s) => a + s.avg_position, 0) / prev28.length : null
  const hasSnapshots = sortedSnaps.length > 0
  const byType = scans ? getLatestScoreByType(scans) : { onpage: null, tech_audit: null, site_crawl: null }
  const latestAuditScore = contentAudits?.find(a => a.status === 'completed')?.overall_score ?? null
  const avgScore = getAvgScore({ ...byType, content_audit: latestAuditScore })

  // Only the latest completed audit per page — older audits get superseded
  // (especially now that the weekly refresh re-audits every tracked page),
  // so their instructions would otherwise linger here as stale advice.
  // contentAudits is already newest-first (API orders by created_at desc).
  const latestAuditByPage = new Map<string, ContentAudit>()
  for (const a of contentAudits ?? []) {
    if (a.status !== 'completed' || !a.results?.claude_code_instructions?.length) continue
    if (!latestAuditByPage.has(a.page_url)) latestAuditByPage.set(a.page_url, a)
  }
  const allClaudeInstructions: ClaudeCodeInstruction[] = Array.from(latestAuditByPage.values())
    .flatMap(a => a.results!.claude_code_instructions)

  const hasRunningSecScan = securityScans?.some(s => s.status === 'pending' || s.status === 'running')

  // Loads the findings the Claude tab's SEO prompt needs. Used to be tied to
  // a "Claude instructies" button in the Scans tab; the tab now triggers it,
  // so the prompt is populated wherever you arrive from.
  const loadSeoFindingsForPrompt = async () => {
    if (!dossier || !scans) return
    setSeoClaudeLoading(true)
    try {
      const result: Record<string, SeoFinding[]> = {}
      for (const type of ['onpage', 'tech_audit', 'site_crawl']) {
        const scan = scans.find(s => s.scan_type === type && s.status === 'completed')
        if (scan) result[type] = await seoApi.getScanFindings(scan.id, { limit: 500 })
      }
      setSeoFindingsByType(result)
    } catch { /* empty */ }
    finally { setSeoClaudeLoading(false) }
  }

  const loadSecFindingsForPrompt = async () => {
    if (!dossier || !securityScans) return
    setSecClaudeLoading(true)
    try {
      const latest = securityScans.find(s => s.status === 'completed')
      if (latest) {
        const findings = await dossiersApi.getSecurityFindings(dossier.id, latest.id)
        setSecFindingsForPrompt(findings)
      }
    } catch { /* empty */ }
    finally { setSecClaudeLoading(false) }
  }

  const handleStartSecurityScan = async () => {
    if (!dossier) return
    setStartingSecurity(true)
    try {
      await dossiersApi.startSecurityScan(dossier.id, secScanType)
      toast.success('Security scan gestart', { description: `${secScanType} voor ${dossier.url}` })
      delayedRefetch(3000)
    } catch (err) {
      toast.error('Scan starten mislukt', { description: err instanceof Error ? err.message : '' })
    } finally {
      setStartingSecurity(false)
    }
  }
  const chartData = buildChartData(snapshots ?? [], scoreHistory ?? [])
  // v1→v2 score-model boundary: the first date the new algorithm produced a
  // score, but only when there is older v1 history to demarcate from. The marker
  // explains the step in the line — old and new scores aren't directly comparable.
  const v2BoundaryLabel = (() => {
    const hist = (scoreHistory ?? [])
      .filter((r) => r.algo_version != null)
      .slice()
      .sort((a, b) => a.date.localeCompare(b.date))
    if (!hist.some((r) => r.algo_version === 1)) return null
    const firstV2 = hist.find((r) => r.algo_version === 2)
    return firstV2 ? format(new Date(firstV2.date), 'd MMM', { locale: nl }) : null
  })()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link to="/dossiers" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3">
          <ArrowLeft className="h-4 w-4" /> Alle dossiers
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold">{dossier.name}</h1>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              {dossier.client_name && <span className="text-muted-foreground text-sm">{dossier.client_name}</span>}
              <a href={dossier.url} target="_blank" rel="noopener noreferrer"
                className="text-sm text-primary hover:underline flex items-center gap-1">
                {dossier.url} <ExternalLink className="h-3 w-3" />
              </a>
              {dossier.baseline_date && (
                <span className="text-xs text-muted-foreground">
                  Gestart: {new Date(dossier.baseline_date).toLocaleDateString('nl-BE')}
                </span>
              )}
            </div>
          </div>
          {/* geen header-knoppen meer — instructies zitten per tab */}
        </div>
      </div>

      {/* GSC integration status — make a missing/broken integration explicit
          instead of silently showing empty search data. */}
      {!dossier.gsc_property && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3">
          <Link2Off className="h-5 w-5 shrink-0 text-amber-500" />
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Geen GSC-property gekoppeld.</span>{' '}
            Koppel een Search Console-property aan dit dossier (via Bewerken) zodat clicks,
            impressies en posities in de grafieken verschijnen.
          </p>
        </div>
      )}
      {dossier.gsc_property && gscStatus && !gscStatus.configured && (
        <div className="flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/30 p-3">
          <AlertCircle className="h-5 w-5 shrink-0 text-red-500" />
          <p className="text-sm">
            <span className="font-medium">Google Search Console niet verbonden.</span>{' '}
            De GSC-data van dit dossier wordt niet bijgewerkt.{' '}
            <Link to="/settings" className="text-primary underline">Verbind Google Search Console</Link>.
          </p>
        </div>
      )}
      {dossier.gsc_property && gscStatus?.configured && gscStatus?.reconnect_required && (
        <div className="flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/30 p-3">
          <AlertCircle className="h-5 w-5 shrink-0 text-red-500" />
          <p className="text-sm">
            <span className="font-medium">GSC-verbinding verlopen.</span>{' '}
            De zoekdata stopt met bijwerken tot je{' '}
            <Link to="/settings" className="text-primary underline">opnieuw verbindt</Link>.
          </p>
        </div>
      )}

      {/* Skip-scanning banner */}
      {dossier.skip_scanning && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30 p-3">
          <PauseCircle className="h-5 w-5 shrink-0 text-amber-500" />
          <p className="flex-1 text-sm">
            <span className="font-medium text-foreground">Scanning gepauzeerd.</span>{' '}
            Nachtelijke SEO- en content-scans worden overgeslagen voor dit dossier.
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              updateDossier.mutate(
                { id: dossierId, data: { skip_scanning: false } },
                { onSuccess: () => toast.success('Scanning hervat') },
              )
            }
          >
            Hervat scanning
          </Button>
        </div>
      )}

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Combined SEO score */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Gemiddelde SEO-score</CardTitle>
          </CardHeader>
          <CardContent>
            <p className={cn('text-3xl font-bold', scoreColor(avgScore))}>
              {avgScore ?? '—'}
            </p>
            <div className="mt-2 flex gap-2 flex-wrap">
              {Object.entries(byType).map(([type, score]) => (
                <span key={type} className={cn('text-xs', scoreColor(score))}>
                  {SCAN_TYPE_LABELS[type]}: {score ?? '—'}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Security stats */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <Shield className="h-4 w-4 text-orange-500" /> Security
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const latestSec = securityScans?.find(s => s.status === 'completed')
              if (!latestSec) return <p className="text-2xl text-muted-foreground">—</p>
              if (latestSec.critical_count > 0)
                return <p className="text-3xl font-bold text-red-600 dark:text-red-300">{latestSec.critical_count}C</p>
              if (latestSec.high_count > 0)
                return <p className="text-3xl font-bold text-orange-500">{latestSec.high_count}H</p>
              return <p className="text-3xl font-bold text-green-600 dark:text-green-300">✓</p>
            })()}
            <p className="text-xs text-muted-foreground mt-1">
              {securityScans?.find(s => s.status === 'completed')
                ? `${securityScans.find(s => s.status === 'completed')!.total_findings} bevindingen`
                : 'Geen scan'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">GSC Clicks (28d)</CardTitle></CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{hasSnapshots ? clicks28.toLocaleString('nl') : '—'}</p>
            {prev28.length > 0 && (
              <TrendChip current={clicks28} previous={sumPrev28('total_clicks')} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Gem. Positie (28d)</CardTitle></CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{avgPos28 !== null ? avgPos28.toFixed(1) : '—'}</p>
            {avgPos28 !== null && avgPosPrev28 !== null && (
              <TrendChip current={-avgPos28} previous={-avgPosPrev28} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Impressies (28d)</CardTitle></CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{hasSnapshots ? impressions28.toLocaleString('nl') : '—'}</p>
            {prev28.length > 0 && (
              <TrendChip current={impressions28} previous={sumPrev28('total_impressions')} />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b flex-wrap">
        {([
          { key: 'overzicht', label: 'Evolutie' },
          { key: 'scans', label: `Scans${scans?.length ? ` (${scans.length})` : ''}` },
          { key: 'security', label: `Security${securityScans?.length ? ` (${securityScans.length})` : ''}` },
          { key: 'snapshots', label: 'GSC Data' },
          { key: 'indexeerbaarheid', label: 'Indexeerbaarheid' },
          { key: 'content', label: 'Content' },
          { key: 'claude', label: `Claude${allClaudeInstructions.length > 0 ? ` (${allClaudeInstructions.length})` : ''}` },
        ] as const).map(({ key, label }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={cn('px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}>
            {label}
          </button>
        ))}

        {/* Concurrenten and WordPress are per-dossier opt-ins that are mostly
            not set up. Behind a dropdown they stop competing with the tabs you
            open every visit, without disappearing. */}
        <div className="relative ml-auto">
          <button
            onClick={() => setMoreOpen((v) => !v)}
            className={cn(
              'flex items-center gap-1 border-b-2 -mb-px px-4 py-2 text-sm font-medium transition-colors',
              MORE_TABS.some((m) => m.key === activeTab)
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {MORE_TABS.find((m) => m.key === activeTab)?.label ?? 'Meer'}
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          {moreOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMoreOpen(false)} />
              <div className="absolute right-0 z-20 mt-1 w-48 rounded-md border bg-popover p-1 shadow-md">
                {dossier.platform_type &&
                  INVENTORY_PLATFORMS.includes(dossier.platform_type) && (
                    <Link
                      to={`/inventory/${dossier.id}`}
                      onClick={() => setMoreOpen(false)}
                      className="flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm transition-colors hover:bg-muted"
                    >
                      Inventaris (SBOM)
                      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                    </Link>
                  )}
                {MORE_TABS.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => {
                      setActiveTab(key)
                      setMoreOpen(false)
                    }}
                    className={cn(
                      'flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm transition-colors hover:bg-muted',
                      activeTab === key && 'bg-muted font-medium',
                    )}
                  >
                    {label}
                    {key === 'concurrenten' && dossier.competitors?.length ? (
                      <span className="text-xs text-muted-foreground">
                        {dossier.competitors.length}
                      </span>
                    ) : null}
                    {key === 'wordpress' && wpSite?.connected ? (
                      <span className="text-xs text-green-600 dark:text-green-300">✓</span>
                    ) : null}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Evolutie ── */}
      {activeTab === 'overzicht' && (
        <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle>Evolutie over tijd</CardTitle>
                <CardDescription>GSC clicks (links) vs SEO-score (rechts, blauwe punten per scan)</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Select value={String(chartDays)} onChange={e => setChartDays(Number(e.target.value))}>
                  <option value="30">30 dagen</option>
                  <option value="90">90 dagen</option>
                  <option value="180">6 maanden</option>
                  <option value="365">1 jaar</option>
                </Select>
                {dossier.gsc_property && (
                  <Button onClick={handleBackfill} disabled={backfilling} size="sm" variant="outline"
                    title="Haalt de per-dag GSC-data tot vandaag op en corrigeert snapshots (geen SEO-scans)">
                    {backfilling ? <Spinner size="sm" className="mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                    GSC bijwerken
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {snapshotsLoading ? (
              <div className="flex justify-center py-12"><Spinner /></div>
            ) : chartData.length === 0 ? (
              <div className="py-12 text-center space-y-2">
                <p className="text-sm text-muted-foreground">Nog geen evolutie-data beschikbaar.</p>
                {dossier.gsc_property ? (
                  <>
                    <p className="text-xs text-muted-foreground">
                      Klik <strong>GSC bijwerken</strong> om de GSC-data op te halen. Data verschijnt na ±10 seconden.
                    </p>
                    <Button size="sm" variant="outline" onClick={handleBackfill} disabled={backfilling} className="mt-2">
                      {backfilling ? <Spinner size="sm" className="mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                      GSC bijwerken
                    </Button>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Koppel eerst een GSC-property aan dit dossier (via Bewerken) voor zoekdata in de grafiek.
                  </p>
                )}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} tickLine={false} interval="preserveStartEnd" />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickLine={false} axisLine={false}
                    label={{ value: 'Clicks', angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 11 } }} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} tickLine={false} axisLine={false}
                    label={{ value: 'SEO Score', angle: 90, position: 'insideRight', offset: 10, style: { fontSize: 11 } }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {v2BoundaryLabel && (
                    <ReferenceLine
                      yAxisId="right"
                      x={v2BoundaryLabel}
                      stroke="#a855f7"
                      strokeDasharray="4 2"
                      label={{ value: 'v2-score', position: 'insideTopRight', fontSize: 10, fill: '#a855f7' }}
                    />
                  )}
                  <Line yAxisId="left" type="monotone" dataKey="clicks" name="Clicks"
                    stroke="#f97316" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  <Line yAxisId="left" type="monotone" dataKey="impressions" name="Impressies"
                    stroke="#94a3b8" strokeWidth={1} strokeDasharray="4 4" dot={false} />
                  <Scatter yAxisId="right" dataKey="seoScore" name="SEO Score" fill="#3b82f6" shape="circle" />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Position evolution — its own chart with a VISIBLE axis so the value
            matches the height (no reversed/hidden-axis confusion). Lower = better. */}
        <Card>
          <CardHeader>
            <CardTitle>Gemiddelde positie</CardTitle>
            <CardDescription>GSC gemiddelde zoekpositie per dag · lager = beter</CardDescription>
          </CardHeader>
          <CardContent>
            {chartData.some(d => d.position != null) ? (
              <ResponsiveContainer width="100%" height={200}>
                <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} tickLine={false} interval="preserveStartEnd" />
                  <YAxis domain={['dataMin - 1', 'dataMax + 1']} allowDecimals={false}
                    tick={{ fontSize: 11 }} tickLine={false} axisLine={false}
                    label={{ value: 'Positie', angle: -90, position: 'insideLeft', offset: 10, style: { fontSize: 11 } }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Line type="monotone" dataKey="position" name="Gem. positie"
                    stroke="#a855f7" strokeWidth={2} dot={false} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">Nog geen positie-data beschikbaar.</p>
            )}
          </CardContent>
        </Card>
        </div>
      )}

      {/* ── Scans ── */}
      {activeTab === 'scans' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle>Scan overzicht</CardTitle>
                  <CardDescription>Laatste run per scan-type — klik om geschiedenis te tonen</CardDescription>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={dossier.skip_scanning ? 'text-amber-600 dark:text-amber-300' : 'text-muted-foreground'}
                    title={dossier.skip_scanning ? 'Scanning is gepauzeerd — klik om te hervatten' : 'Pauzeer nachtelijke scans voor dit dossier'}
                    onClick={() =>
                      updateDossier.mutate(
                        { id: dossierId, data: { skip_scanning: !dossier.skip_scanning } },
                        {
                          onSuccess: () =>
                            toast.success(dossier.skip_scanning ? 'Scanning hervat' : 'Scanning gepauzeerd'),
                        },
                      )
                    }
                  >
                    <PauseCircle className="h-4 w-4 mr-1" />
                    {dossier.skip_scanning ? 'Gepauzeerd' : 'Pauzeer'}
                  </Button>
                  {selectedScanTypes.size > 0 ? (
                    <Button onClick={handleTriggerSelected} disabled={scanningAll} size="sm">
                      {scanningAll ? <Spinner size="sm" className="mr-1.5" /> : <Play className="h-4 w-4 mr-1.5" />}
                      Start geselecteerde ({selectedScanTypes.size}×)
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">Vink scans aan om te starten</span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {scanSummaryLoading ? (
                <div className="flex justify-center p-8"><Spinner /></div>
              ) : !scanSummary?.length ? (
                <div className="p-8 text-center">
                  <p className="text-sm text-muted-foreground mb-3">Nog geen scans voor dit dossier.</p>
                  <Button onClick={handleScanAll} disabled={scanningAll} size="sm">
                    <Play className="h-4 w-4 mr-1.5" /> Alle 3 SEO-scans starten
                  </Button>
                </div>
              ) : (
                <div className="divide-y">
                  {scanSummary.map((entry: ScanTypeSummary) => {
                    const isExpanded = expandedScanType === entry.type_key
                    const lat = entry.latest
                    const isActive = lat?.status === 'pending' || lat?.status === 'running'
                    const catColor: Record<string, string> = {
                      seo: 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300',
                      security: 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300',
                      gsc: 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300',
                      content: 'bg-purple-50 text-purple-700',
                    }
                    return (
                      <div key={entry.type_key}>
                        <div className="flex items-center">
                          {/* Checkbox — stopPropagation so it doesn't toggle history */}
                          <label className="flex items-center px-3 py-3 cursor-pointer" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 accent-primary"
                              checked={selectedScanTypes.has(entry.type_key)}
                              onChange={e => {
                                const next = new Set(selectedScanTypes)
                                if (e.target.checked) next.add(entry.type_key)
                                else next.delete(entry.type_key)
                                setSelectedScanTypes(next)
                              }}
                            />
                          </label>
                        <button
                          className="flex-1 text-left px-2 py-3 flex items-center gap-3 hover:bg-muted/30 transition-colors"
                          onClick={() => setExpandedScanType(isExpanded ? null : entry.type_key)}
                        >
                          {/* Category badge */}
                          <span className={cn('shrink-0 text-xs font-medium px-1.5 py-0.5 rounded', catColor[entry.category] ?? 'bg-slate-100 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300')}>
                            {entry.category}
                          </span>

                          {/* Name + optional failure reason */}
                          <span className="flex-1 min-w-0">
                            <span className="text-sm font-medium">{entry.display_name}</span>
                            {lat?.status === 'failed' && lat.error_message && (
                              <span className="block text-xs text-red-500 truncate max-w-xs" title={lat.error_message}>
                                {lat.error_message}
                              </span>
                            )}
                          </span>

                          {/* Status / score */}
                          {!lat ? (
                            <span className="text-xs text-muted-foreground italic">nooit uitgevoerd</span>
                          ) : isActive ? (
                            <span className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-300">
                              <Spinner className="h-3 w-3" />
                              {lat.status === 'pending' ? 'In de wacht' : `Bezig${lat.progress_percent != null ? ` ${lat.progress_percent}%` : ''}`}
                            </span>
                          ) : (
                            <div className="flex items-center gap-3 shrink-0">
                              {/* Findings summary */}
                              {entry.category === 'seo' && lat.critical_count != null && lat.critical_count > 0 && (
                                <span className="text-xs text-red-600 dark:text-red-300">{lat.critical_count} krit.</span>
                              )}
                              {entry.category === 'seo' && lat.warning_count != null && lat.warning_count > 0 && (
                                <span className="text-xs text-orange-500">{lat.warning_count} warn.</span>
                              )}
                              {entry.category === 'security' && lat.total_findings != null && lat.total_findings > 0 && (
                                <span className="text-xs text-red-600 dark:text-red-300">{lat.total_findings} bevindingen</span>
                              )}
                              {entry.category === 'gsc' && lat.total_clicks != null && (
                                <span className="text-xs text-muted-foreground">{lat.total_clicks.toLocaleString('nl')} kliks</span>
                              )}
                              {entry.category === 'content' && lat.page_url && (
                                <span className="text-xs text-muted-foreground truncate max-w-[180px]">
                                  {lat.page_url.replace(/^https?:\/\/[^/]+/, '') || '/'}
                                </span>
                              )}
                              {/* Score */}
                              {lat.score != null && (
                                <span className={cn('text-sm font-bold tabular-nums', scoreColor(lat.score))}>
                                  {lat.score}<span className="text-xs font-normal">/100</span>
                                </span>
                              )}
                              {/* Status badge */}
                              <ScanStatusBadge status={lat.status ?? 'completed'} />
                              {/* Date */}
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {lat.created_at
                                  ? formatDistanceToNow(new Date(lat.created_at), { addSuffix: true, locale: nl })
                                  : '—'}
                              </span>
                              {/* Run count */}
                              {entry.total_runs > 1 && (
                                <span className="text-xs text-muted-foreground">#{entry.total_runs}</span>
                              )}
                            </div>
                          )}
                        </button>
                        </div>{/* end flex items-center row */}

                        {/* Expanded history */}
                        {isExpanded && entry.history.length > 0 && (
                          <div className="bg-muted/10 border-t px-4 py-2">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Geschiedenis</p>
                            <div className="divide-y divide-border/50">
                              {entry.history.map((h: ScanItem) => (
                                <div key={h.id} className="py-1.5 text-xs text-muted-foreground">
                                  <div className="flex items-center gap-3">
                                  <ScanStatusBadge status={h.status ?? 'completed'} />
                                  <span className="flex-1">
                                    {h.created_at
                                      ? formatDistanceToNow(new Date(h.created_at), { addSuffix: true, locale: nl })
                                      : '—'}
                                  </span>
                                  {h.score != null && (
                                    <span className={cn('font-medium tabular-nums', scoreColor(h.score))}>
                                      {h.score}/100
                                    </span>
                                  )}
                                  {entry.category === 'gsc' && h.total_clicks != null && (
                                    <span>{h.total_clicks.toLocaleString('nl')} kliks</span>
                                  )}
                                  {entry.category === 'security' && h.total_findings != null && (
                                    <span>{h.total_findings} bevindingen</span>
                                  )}
                                  {entry.category === 'content' && h.page_url && (
                                    <span className="truncate max-w-[200px]">
                                      {h.page_url.replace(/^https?:\/\/[^/]+/, '') || '/'}
                                    </span>
                                  )}
                                  </div>
                                  {h.status === 'failed' && h.error_message && (
                                    <p className="mt-0.5 pl-1 text-red-500 break-all">{h.error_message}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {isExpanded && entry.history.length === 0 && entry.latest && (
                          <div className="bg-muted/10 border-t px-4 py-2 text-xs text-muted-foreground">
                            Enkel de meest recente run beschikbaar.
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Scanschema ── */}
          <Card>
            <CardHeader>
              <CardTitle>Scanschema</CardTitle>
              <CardDescription>
                Stel per scan-type in of en hoe vaak hij automatisch draait. De nachtelijke dispatcher
                (02:00) controleert dit schema en slaat over als de vorige run nog niet oud genoeg is.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                const display = localSchedules ?? scanSchedules ?? []
                const INTERVAL_OPTS = [
                  { value: 1,  label: 'Dagelijks' },
                  { value: 7,  label: 'Wekelijks' },
                  { value: 14, label: 'Tweewekelijks' },
                  { value: 30, label: 'Maandelijks' },
                ]
                const catColor: Record<string, string> = {
                  seo: 'text-blue-600 dark:text-blue-300',
                  security: 'text-red-600 dark:text-red-300',
                }
                return (
                  <div className="space-y-3">
                    <div className="divide-y">
                      {display.map((s: ScanSchedule) => (
                        <div key={s.scan_type} className="flex items-center gap-4 py-3">
                          <button
                            type="button"
                            onClick={() => {
                              const next = (localSchedules ?? scanSchedules ?? []).map(x =>
                                x.scan_type === s.scan_type ? { ...x, enabled: !x.enabled } : x
                              )
                              setLocalSchedules(next)
                              setSchedulesDirty(true)
                            }}
                            className={cn(
                              'shrink-0 w-9 h-5 rounded-full transition-colors relative',
                              s.enabled ? 'bg-primary' : 'bg-slate-200',
                            )}
                            aria-label={s.enabled ? 'Uitschakelen' : 'Inschakelen'}
                          >
                            <span className={cn(
                              'absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform',
                              s.enabled && 'translate-x-4',
                            )} />
                          </button>

                          <span className={cn('flex-1 text-sm font-medium', catColor[s.category] ?? '')}>
                            {s.display_name}
                          </span>

                          <Select
                            value={String(s.interval_days)}
                            onChange={e => {
                              const next = (localSchedules ?? scanSchedules ?? []).map(x =>
                                x.scan_type === s.scan_type ? { ...x, interval_days: Number(e.target.value) } : x
                              )
                              setLocalSchedules(next)
                              setSchedulesDirty(true)
                            }}
                            className="w-36 text-sm"
                            disabled={!s.enabled}
                          >
                            {INTERVAL_OPTS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </Select>

                          <span className="text-xs text-muted-foreground w-32 text-right">
                            {s.last_triggered_at
                              ? formatDistanceToNow(new Date(s.last_triggered_at), { addSuffix: true, locale: nl })
                              : 'nooit'}
                          </span>
                        </div>
                      ))}
                    </div>

                    {schedulesDirty && (
                      <div className="flex items-center justify-end gap-2 pt-2 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setLocalSchedules(null); setSchedulesDirty(false) }}
                        >
                          Annuleren
                        </Button>
                        <Button
                          size="sm"
                          disabled={updateSchedules.isPending}
                          onClick={() => {
                            if (!localSchedules) return
                            updateSchedules.mutate(
                              localSchedules.map(s => ({
                                scan_type: s.scan_type,
                                enabled: s.enabled,
                                interval_days: s.interval_days,
                              })),
                              {
                                onSuccess: () => { setLocalSchedules(null); setSchedulesDirty(false); toast.success('Schema opgeslagen') },
                                onError: () => toast.error('Opslaan mislukt'),
                              }
                            )
                          }}
                        >
                          {updateSchedules.isPending ? <Spinner className="h-4 w-4 mr-1" /> : null}
                          Opslaan
                        </Button>
                      </div>
                    )}
                  </div>
                )
              })()}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Security ── */}
      {activeTab === 'security' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-orange-500" /> Security Scans
                </CardTitle>
                <CardDescription>ZAP en Nuclei scans gelinkt aan dit dossier</CardDescription>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={secScanType} onChange={e => setSecScanType(e.target.value)}>
                  <option value="zap_baseline">ZAP Baseline (3–5 min)</option>
                  <option value="zap_full">ZAP Full (15–30 min)</option>
                  <option value="nuclei">Nuclei (5–15 min)</option>
                </Select>
                <Button onClick={handleStartSecurityScan} disabled={startingSecurity || hasRunningSecScan} size="sm">
                  {startingSecurity ? <Spinner size="sm" className="mr-1.5" /> : <Play className="h-4 w-4 mr-1.5" />}
                  Scan starten
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {secScansLoading ? (
              <div className="flex justify-center p-8"><Spinner /></div>
            ) : !securityScans?.length ? (
              <div className="p-8 text-center">
                <p className="text-sm text-muted-foreground mb-3">Nog geen security scans voor dit dossier.</p>
              </div>
            ) : (
              <div className="divide-y max-h-[700px] overflow-y-auto">
                {securityScans.map(scan => (
                  <SecurityScanRow key={scan.id} scan={scan} dossierId={dossierId} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── GSC Data ── */}
      {activeTab === 'snapshots' && (
        <div className="space-y-6">

        <GscTools gscProperty={dossier.gsc_property ?? null} />

        {/* GSC Trends: KPI delta + 12-week timeline + bucket migration */}
        {gscTrendsLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : gscTrendsError ? (
          <Card>
            <CardContent className="p-4 text-sm text-muted-foreground flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0 text-amber-500" />
              GSC trends konden niet worden geladen. Vernieuw de pagina om opnieuw te proberen.
            </CardContent>
          </Card>
        ) : gscTrends?.has_gsc ? (
          <GscTrendsSection trends={gscTrends} />
        ) : dossier.gsc_property ? (
          <Card>
            <CardContent className="p-4 text-sm text-muted-foreground">
              Nog geen GSC snapshot-data beschikbaar voor trends. Klik "GSC bijwerken" in het Evolutie-tab.
            </CardContent>
          </Card>
        ) : null}

        {/* Position distribution + cannibalization (R11) */}
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <CardTitle>Positieverdeling (laatste 28 dagen)</CardTitle>
                <CardDescription>
                  Verdeling van zoektermen over rang-buckets — eerlijker dan de impressie-gewogen
                  "gemiddelde positie". Plus keyword-kannibalisatie (meerdere pagina's per zoekterm in de top 20).
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {positionBucketsLoading ? (
              <div className="flex justify-center p-8"><Spinner /></div>
            ) : !positionBuckets?.has_gsc ? (
              <p className="p-6 text-center text-sm text-muted-foreground">
                Koppel een GSC-property aan dit dossier voor positiedata.
              </p>
            ) : (
              <div className="space-y-4">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={POSITION_BANDS.map((b) => ({
                      label: b.label,
                      keywords: positionBuckets.buckets[b.key]?.keywords ?? 0,
                      color: b.color,
                    }))}
                    margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip
                      formatter={(v: number) => [`${v} zoektermen`, 'Aantal']}
                      contentStyle={{ fontSize: 12 }}
                    />
                    <Bar dataKey="keywords" radius={[4, 4, 0, 0]}>
                      {POSITION_BANDS.map((b) => (
                        <Cell key={b.key} fill={b.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>

                {positionBuckets.cannibalization.length > 0 && (
                  <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3">
                    <div className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-amber-800 dark:text-amber-300">
                      <AlertCircle className="h-4 w-4" />
                      {positionBuckets.cannibalization.length} zoekterm(en) met kannibalisatie
                    </div>
                    <ul className="space-y-1 text-xs text-amber-900">
                      {positionBuckets.cannibalization.slice(0, 8).map((c) => (
                        <li key={c.query}>
                          <span className="font-medium">"{c.query}"</span> — {c.pages.length} pagina's:{' '}
                          {c.pages.slice(0, 3).map((p) => `${p.page} (#${p.position})`).join(', ')}
                          {c.pages.length > 3 ? '…' : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div>
              <CardTitle>Dagelijkse GSC data</CardTitle>
              <CardDescription>Rolling 28-dagen gemiddelde per snapshot · GSC bijwerken via Evolutie-tab</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {snapshotsLoading ? (
              <div className="flex justify-center p-8"><Spinner /></div>
            ) : !snapshots?.length ? (
              <div className="p-8 text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  Nog geen snapshots. {dossier.gsc_property ? 'Klik "Nu ophalen" om te synchroniseren.' : 'Sla eerst een GSC property op in het dossier.'}
                </p>
                {!dossier.gsc_property && (
                  <p className="text-xs text-muted-foreground">
                    Ga naar <Link to="/dossiers" className="text-primary underline">Dossiers</Link>, bewerk dit dossier en vul de GSC Property URL in.
                  </p>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground">Datum</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">Clicks</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">Impressies</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">CTR</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">Positie</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {[...snapshots].reverse().map((s: GscSnapshotData) => (
                      <tr key={s.id} className="hover:bg-muted/30">
                        <td className="px-4 py-2">{new Date(s.snapshot_date).toLocaleDateString('nl-BE')}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{s.total_clicks.toLocaleString('nl')}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{s.total_impressions.toLocaleString('nl')}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{s.avg_ctr}%</td>
                        <td className="px-4 py-2 text-right tabular-nums">{s.avg_position.toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      )}

      {/* ── Indexeerbaarheid (R10) ── */}
      {activeTab === 'indexeerbaarheid' && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <CardTitle>Indexeerbaarheidsrapport</CardTitle>
                <CardDescription>
                  Eén status per gecrawlde pagina · synthese van de laatste site-crawl + GSC URL-inspectie
                  {indexReport?.crawled_at
                    ? ` · crawl van ${new Date(indexReport.crawled_at).toLocaleDateString('nl-BE')}`
                    : ''}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {!indexReport ? (
              <div className="flex justify-center p-8"><Spinner /></div>
            ) : !indexReport.has_crawl ? (
              <p className="p-8 text-center text-sm text-muted-foreground">
                Nog geen site-crawl voor dit dossier. Start een site-crawl (of wacht op de nachtelijke
                run); daarna verschijnt hier de indexstatus per pagina.
              </p>
            ) : (
              <>
                {/* Bucket count cards */}
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                  {INDEX_BUCKETS.map((b) => (
                    <div key={b.key} className={cn('rounded-lg border p-3 text-center', b.cls)}>
                      <div className="text-2xl font-bold tabular-nums">
                        {indexReport.buckets[b.key] ?? 0}
                      </div>
                      <div className="text-xs">{b.label}</div>
                    </div>
                  ))}
                </div>

                {/* Context counts (not analysed pages) */}
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                  <span>Totaal geanalyseerd: <strong className="tabular-nums">{indexReport.total}</strong></span>
                  <span>Redirects: <strong className="tabular-nums">{indexReport.redirected}</strong></span>
                  <span>Robots-geblokkeerd: <strong className="tabular-nums">{indexReport.robots_blocked}</strong></span>
                </div>

                {/* Per-URL: redirects + robots-blocked (GSC: "Page with redirect" / "Blocked by robots.txt") */}
                {indexReport.redirected_urls.length > 0 && (
                  <details className="rounded-lg border">
                    <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
                      Redirects ({indexReport.redirected})
                    </summary>
                    <ul className="max-h-64 space-y-0.5 overflow-y-auto border-t px-3 py-2 text-xs">
                      {indexReport.redirected_urls.map((r) => (
                        <li key={r.url} className="truncate" title={`${r.url} → ${r.to}`}>
                          <span className="text-muted-foreground">{r.url}</span> → {r.to}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {indexReport.robots_blocked_urls.length > 0 && (
                  <details className="rounded-lg border">
                    <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
                      Robots-geblokkeerd ({indexReport.robots_blocked})
                    </summary>
                    <ul className="max-h-64 space-y-0.5 overflow-y-auto border-t px-3 py-2 text-xs">
                      {indexReport.robots_blocked_urls.map((u) => (
                        <li key={u} className="truncate" title={u}>{u}</li>
                      ))}
                    </ul>
                  </details>
                )}

                {/* Conflicts */}
                {indexReport.conflicts.length > 0 && (
                  <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3">
                    <div className="mb-1 flex items-center gap-1.5 text-sm font-medium text-amber-800 dark:text-amber-300">
                      <AlertCircle className="h-4 w-4" />
                      {indexReport.conflicts.length} conflict(en): pagina krijgt GSC-verkeer maar is niet-indexeerbaar
                    </div>
                    <ul className="space-y-0.5 text-xs text-amber-900">
                      {indexReport.conflicts.slice(0, 10).map((c) => (
                        <li key={c.url} className="truncate">
                          <span className="font-medium">{INDEX_BUCKET_LABEL[c.bucket] ?? c.bucket}</span> — {c.url}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Per-page table */}
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Pagina</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Status</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">HTTP</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {indexReport.pages.slice(0, 250).map((p) => (
                        <tr key={p.url} className="hover:bg-muted/30">
                          <td className="max-w-0 px-3 py-2">
                            <span className="block truncate" title={p.url}>{p.url}</span>
                          </td>
                          <td className="px-3 py-2">
                            <span className={cn('inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium', INDEX_BUCKET_CLS[p.bucket] ?? 'border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300')}>
                              {INDEX_BUCKET_LABEL[p.bucket] ?? p.bucket}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{p.status ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {indexReport.pages.length > 250 && (
                    <div className="border-t px-3 py-2 text-center text-xs text-muted-foreground">
                      Eerste 250 van {indexReport.pages.length} pagina's getoond.
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Concurrenten (R22) ── */}
      {activeTab === 'concurrenten' && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <CardTitle>Concurrenten</CardTitle>
                <CardDescription>
                  Vergelijk per pijler met concurrent-sites — een onpage-benchmark van hun homepage
                  (geen volledige crawl). Dezelfde 7 pijlers, een andere site.
                </CardDescription>
              </div>
              {(dossier.competitors?.length ?? 0) > 0 && (
                <Button
                  size="sm"
                  onClick={() =>
                    scanCompetitors.mutate(dossierId, {
                      onSuccess: (r) => toast.success(`${r.competitors} concurrent-scan(s) gestart`),
                      onError: () => toast.error('Scannen mislukt'),
                    })
                  }
                  disabled={scanCompetitors.isPending}
                >
                  <Play className="mr-1 h-3.5 w-3.5" /> Scan concurrenten
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* URL management */}
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  placeholder="https://concurrent.be"
                  value={newCompetitor}
                  onChange={(e) => setNewCompetitor(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addCompetitor()
                    }
                  }}
                />
                <Button variant="outline" size="sm" onClick={addCompetitor}>Toevoegen</Button>
              </div>
              {(dossier.competitors?.length ?? 0) > 0 ? (
                <ul className="space-y-1">
                  {dossier.competitors.map((u) => (
                    <li key={u} className="flex items-center justify-between rounded border px-2 py-1 text-sm">
                      <span className="truncate">{u}</span>
                      <button
                        onClick={() => removeCompetitor(u)}
                        title="Verwijderen"
                        className="shrink-0 text-muted-foreground hover:text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Nog geen concurrenten. Voeg een URL toe en klik "Scan concurrenten".
                </p>
              )}
            </div>

            {/* Per-pillar comparison */}
            {competitorComparison && competitorComparison.competitors.some((c) => c.pillar_scores) && (
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Pijler</th>
                      <th className="px-3 py-2 text-center text-xs font-medium">Jouw site</th>
                      {competitorComparison.competitors.map((c) => (
                        <th key={c.url} className="px-3 py-2 text-center text-xs font-medium text-muted-foreground">
                          <span className="block max-w-[140px] truncate" title={c.url}>{hostOf(c.url)}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr className="font-semibold">
                      <td className="px-3 py-2">Totaal</td>
                      <td className={cn('px-3 py-2 text-center tabular-nums', scoreColor(competitorComparison.dossier.score))}>
                        {competitorComparison.dossier.score ?? '—'}
                      </td>
                      {competitorComparison.competitors.map((c) => (
                        <td key={c.url} className={cn('px-3 py-2 text-center tabular-nums', scoreColor(c.score))}>
                          {c.score ?? '—'}
                        </td>
                      ))}
                    </tr>
                    {SEO_PILLARS.map((p) => (
                      <tr key={p.key}>
                        <td className="px-3 py-2 text-muted-foreground">{p.label}</td>
                        <td className="px-3 py-2 text-center tabular-nums">
                          {competitorComparison.dossier.pillar_scores?.[p.key] ?? '—'}
                        </td>
                        {competitorComparison.competitors.map((c) => (
                          <td key={c.url} className="px-3 py-2 text-center tabular-nums">
                            {c.pillar_scores?.[p.key] ?? '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Content tab ── */}
      {activeTab === 'content' && (
        <div className="space-y-6">

          {/* Topicale gaps — pure GSC, geen LLM */}
          <Card>
            <CardHeader>
              <CardTitle>Topicale gaps</CardTitle>
              <CardDescription>
                Automatisch berekend op basis van GSC-zoekwoorddata — geen LLM vereist.
                Wordt elke nacht bijgewerkt om 08:30.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!topicalGaps?.has_data ? (
                <p className="text-sm text-muted-foreground">
                  Nog geen topicale gap-analyse beschikbaar. Vereist minstens 28 dagen GSC-data.
                </p>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Berekend op {topicalGaps.computed_at
                      ? format(new Date(topicalGaps.computed_at), 'd MMM yyyy HH:mm', { locale: nl })
                      : '—'}
                    {' · '}{topicalGaps.gaps?.length ?? 0} gaps gevonden
                  </p>
                  {(topicalGaps.gaps ?? []).length === 0 ? (
                    <p className="text-sm text-green-600 dark:text-green-300">Geen significante gaps gevonden.</p>
                  ) : (
                    <div className="divide-y">
                      {(topicalGaps.gaps ?? []).map((gap: TopicalGap, i: number) => (
                        <div key={i} className="py-3 flex items-start gap-3">
                          <span className={cn(
                            'mt-0.5 shrink-0 text-xs font-medium px-2 py-0.5 rounded-full',
                            gap.impact === 'hoog' && 'bg-red-100 dark:bg-red-950/30 text-red-700 dark:text-red-300',
                            gap.impact === 'gemiddeld' && 'bg-amber-100 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300',
                            gap.impact === 'laag' && 'bg-slate-100 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300',
                          )}>
                            {gap.impact}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              &ldquo;{gap.keyword}&rdquo;
                              <span className="ml-2 text-xs text-muted-foreground font-normal">
                                {gap.impressions.toLocaleString('nl')} indrukken · pos {gap.position}
                              </span>
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">{gap.issue}</p>
                            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">{gap.suggestion}</p>
                          </div>
                          <span className={cn(
                            'shrink-0 text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800/40 text-slate-500',
                          )}>
                            {gap.type === 'high_volume_low_rank' && 'zwakke positie'}
                            {gap.type === 'near_page_one' && 'quick win'}
                            {gap.type === 'low_ctr' && 'lage CTR'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* LLM content audit */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle>Content audit</CardTitle>
                  <CardDescription>
                    AI-analyse van pagina-inhoud op basis van GSC-data.
                    Kies een pagina en start een analyse (± 30 seconden).
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={selectedAuditPage}
                    onChange={(e) => setSelectedAuditPage(e.target.value)}
                    className="w-72 text-sm"
                  >
                    <option value="">— Kies een pagina —</option>
                    {(gscPages ?? []).map((p) => (
                      <option key={p.page} value={p.page}>
                        {p.page.replace(/^https?:\/\/[^/]+/, '') || '/'} ({p.impressions.toLocaleString('nl')} indrukken)
                      </option>
                    ))}
                  </Select>
                  <Button
                    size="sm"
                    disabled={!selectedAuditPage || triggerAudit.isPending}
                    onClick={() => {
                      if (!selectedAuditPage) return
                      triggerAudit.mutate(selectedAuditPage, {
                        onSuccess: () => toast.success('Content audit gestart'),
                        onError: () => toast.error('Starten mislukt'),
                      })
                    }}
                  >
                    {triggerAudit.isPending ? <Spinner className="h-4 w-4" /> : <Play className="h-4 w-4 mr-1" />}
                    Analyseer
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={triggerAuditBulk.isPending}
                    title="Start een analyse voor alle pagina's van dit dossier tegelijk"
                    onClick={() => {
                      triggerAuditBulk.mutate(undefined, {
                        onSuccess: (res) => {
                          if (res.dispatched === 0 && res.reason === 'no_known_pages') {
                            toast.error('Nog geen pagina\'s bekend — draai eerst een site crawl of voeg handmatig een pagina toe.')
                          } else if (res.dispatched === 0) {
                            toast.info('Alle bekende pagina\'s hebben al een analyse lopen.')
                          } else {
                            toast.success(
                              `${res.dispatched} pagina${res.dispatched === 1 ? '' : "'s"} in analyse` +
                              (res.skipped > 0 ? ` (${res.skipped} al bezig, overgeslagen)` : '')
                            )
                          }
                        },
                        onError: () => toast.error('Starten van bulk-analyse mislukt'),
                      })
                    }}
                  >
                    {triggerAuditBulk.isPending ? <Spinner className="h-4 w-4" /> : <Play className="h-4 w-4 mr-1" />}
                    Analyseer alle pagina's
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!contentAudits?.length}
                    onClick={() => setShowContentAuditPrompt(true)}
                  >
                    <Bot className="h-4 w-4 mr-1" />
                    Claude Code instructies
                  </Button>
                </div>
              </div>
            </CardHeader>
            {showContentAuditPrompt && dossier && (
              <CardContent className="pt-0">
                <InstructionsPanel
                  title="Claude Code instructies — Content audit"
                  prompt={generateContentAuditPrompt(dossier, contentAudits ?? [])}
                  onClose={() => setShowContentAuditPrompt(false)}
                />
              </CardContent>
            )}
            <CardContent>
              {contentAuditsLoading ? (
                <Spinner />
              ) : !contentAudits?.length ? (
                <p className="text-sm text-muted-foreground">
                  Nog geen content audits uitgevoerd. Kies een pagina hierboven en klik Analyseer.
                </p>
              ) : (
                <div className="space-y-4">
                  {contentAudits.map((audit: ContentAudit) => (
                    <div key={audit.id} className="border rounded-lg overflow-hidden">
                      {/* Audit header */}
                      <button
                        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-muted/30 transition-colors"
                        onClick={() => setExpandedAuditId(expandedAuditId === audit.id ? null : audit.id)}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{audit.page_url}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {audit.created_at
                              ? formatDistanceToNow(new Date(audit.created_at), { addSuffix: true, locale: nl })
                              : ''}
                            {audit.page_title && ` · ${audit.page_title}`}
                          </p>
                        </div>
                        {audit.status === 'pending' || audit.status === 'running' ? (
                          <span className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-300">
                            <Spinner className="h-3 w-3" />
                            {audit.status === 'pending' ? 'In de wacht' : 'Bezig…'}
                          </span>
                        ) : audit.status === 'failed_fetch' ? (
                          <span className="flex items-center gap-1.5" title={audit.error_message ?? undefined}>
                            <AlertCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-300" />
                            <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 dark:text-amber-400">
                              Pagina niet opgehaald
                            </Badge>
                          </span>
                        ) : audit.status === 'failed' ? (
                          <Badge variant="destructive" className="text-xs">Mislukt</Badge>
                        ) : (
                          <>
                            <span className={cn(
                              'text-xs font-medium px-2 py-0.5 rounded-full',
                              (audit.results?.intent_fit === 'goed') && 'bg-green-100 dark:bg-green-950/30 text-green-700 dark:text-green-300',
                              (audit.results?.intent_fit === 'matig') && 'bg-amber-100 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300',
                              (audit.results?.intent_fit === 'slecht') && 'bg-red-100 dark:bg-red-950/30 text-red-700 dark:text-red-300',
                            )}>
                              {audit.results?.intent_fit ?? '—'}
                            </span>
                            <span className="text-2xl font-bold tabular-nums ml-2">
                              {audit.overall_score ?? '—'}
                            </span>
                          </>
                        )}
                      </button>

                      {/* Expanded detail */}
                      {expandedAuditId === audit.id && audit.results && (
                        <div className="border-t px-4 py-4 space-y-5 bg-muted/10">
                          {/* Summary row */}
                          <div className="flex flex-wrap gap-4 text-sm">
                            <span>
                              <span className="text-muted-foreground">Type: </span>
                              <strong>{audit.results.content_type_detected}</strong>
                            </span>
                            <span>
                              <span className="text-muted-foreground">Intentie: </span>
                              <strong>{audit.results.intent_type}</strong>
                            </span>
                            <span>
                              <span className="text-muted-foreground">Woorden: </span>
                              <strong>{audit.word_count?.toLocaleString('nl') ?? '—'}</strong>
                            </span>
                            {audit.results.snippet_opportunity && (
                              <span className="text-blue-700 dark:text-blue-300 font-medium">Featured snippet kans</span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{audit.results.intent_fit_reason}</p>

                          {/* Gaps */}
                          {audit.results.gaps.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                                Gevonden gaps ({audit.results.gaps.length})
                              </p>
                              <div className="space-y-2">
                                {audit.results.gaps.map((gap, i) => (
                                  <div key={i} className="flex items-start gap-2 text-sm">
                                    <span className={cn(
                                      'mt-0.5 shrink-0 text-xs font-medium px-1.5 py-0.5 rounded',
                                      gap.impact === 'hoog' && 'bg-red-100 dark:bg-red-950/30 text-red-700 dark:text-red-300',
                                      gap.impact === 'gemiddeld' && 'bg-amber-100 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300',
                                      gap.impact === 'laag' && 'bg-slate-100 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300',
                                    )}>
                                      {gap.impact}
                                    </span>
                                    <div>
                                      <p className="font-medium">{gap.title}</p>
                                      <p className="text-muted-foreground text-xs">{gap.detail}</p>
                                      <p className="text-blue-700 dark:text-blue-300 text-xs mt-0.5">{gap.suggestion}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Heading suggestions */}
                          {audit.results.heading_suggestions.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                                Koppen & meta
                              </p>
                              <div className="space-y-2">
                                {audit.results.heading_suggestions.map((h, i) => (
                                  <div key={i} className="text-sm bg-slate-50 dark:bg-slate-800/40 rounded p-2">
                                    <span className="text-xs font-mono text-muted-foreground uppercase mr-2">{h.element}</span>
                                    <span className="line-through text-red-600 dark:text-red-300 mr-2">{h.current}</span>
                                    <span className="text-green-700 dark:text-green-300">{h.suggestion}</span>
                                    <p className="text-xs text-muted-foreground mt-0.5">{h.reason}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* FAQs */}
                          {audit.results.faq_suggestions.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                                Ontbrekende FAQ-vragen
                              </p>
                              <ul className="list-disc list-inside space-y-1">
                                {audit.results.faq_suggestions.map((q, i) => (
                                  <li key={i} className="text-sm">{q}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Missing topics */}
                          {audit.results.missing_topics.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                                Ontbrekende onderwerpen
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {audit.results.missing_topics.map((t, i) => (
                                  <span key={i} className="text-xs bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300 px-2 py-0.5 rounded-full">{t}</span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Snippet suggestion */}
                          {audit.results.snippet_opportunity && audit.results.snippet_suggestion && (
                            <div className="bg-blue-50 dark:bg-blue-950/30 rounded p-3 text-sm text-blue-800 dark:text-blue-300">
                              <strong>Featured snippet tip:</strong> {audit.results.snippet_suggestion}
                            </div>
                          )}

                          {/* Claude Code instructions — always visible for completed audits */}
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                              <Bot className="h-3.5 w-3.5" /> Claude Code instructies
                            </p>
                            {audit.results.claude_code_instructions && audit.results.claude_code_instructions.length > 0 ? (
                              <div className="space-y-2">
                                {audit.results.claude_code_instructions.map((instr: ClaudeCodeInstruction, i: number) => (
                                  <ClaudeCodePromptCard key={i} instruction={instr} />
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-muted-foreground italic">
                                Geen instructies in deze audit — start een nieuwe audit om prompts te genereren.
                              </p>
                            )}
                          </div>

                          {/* GSC keywords used as context */}
                          {audit.gsc_keywords && audit.gsc_keywords.length > 0 && (
                            <details className="text-xs text-muted-foreground">
                              <summary className="cursor-pointer hover:text-foreground">
                                GSC-context gebruikt ({audit.gsc_keywords.length} zoekwoorden)
                              </summary>
                              <ul className="mt-2 space-y-0.5">
                                {audit.gsc_keywords.map((kw, i) => (
                                  <li key={i}>&ldquo;{kw.query}&rdquo; — pos {kw.position}, {kw.impressions} indrukken</li>
                                ))}
                              </ul>
                            </details>
                          )}
                        </div>
                      )}

                      {/* Error state */}
                      {expandedAuditId === audit.id && audit.status === 'failed' && (
                        <div className="border-t px-4 py-3 text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-950/30">
                          {audit.error_message || 'Onbekende fout'}
                        </div>
                      )}

                      {/* Failed-fetch state: a technical problem, not a content verdict —
                          never render this as a score. */}
                      {expandedAuditId === audit.id && audit.status === 'failed_fetch' && (
                        <div className="border-t px-4 py-3 text-sm text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400">
                          <p className="font-medium">Pagina kon niet correct opgehaald worden.</p>
                          <p className="mt-0.5">{audit.error_message || 'Onbekende reden'}</p>
                          <p className="mt-1 text-xs text-amber-700/80 dark:text-amber-400/80">
                            Dit is een technisch probleem (blokkade, timeout of lege pagina) — geen
                            inhoudelijk oordeel over deze pagina. Er is geen score opgeslagen.
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Embedding-based gaps — G6 */}
          <Card>
            <CardHeader>
              <CardTitle>Content gaps (embeddings)</CardTitle>
              <CardDescription>
                Zoekvragen met lage CTR (&lt;3%) waarvoor geen goed matchende pagina bestaat,
                op basis van embedding-similariteit. Wordt wekelijks berekend.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!contentGaps?.has_data ? (
                // Two different empty states. They used to read the same, and
                // both pointed at a missing API key — while the real reason
                // was that nothing ever ran the analysis.
                contentGaps && !contentGaps.configured ? (
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">
                      Geen embedding-API-sleutel geconfigureerd.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Zet <code className="rounded bg-muted px-1">VOYAGE_API_KEY</code> of{' '}
                      <code className="rounded bg-muted px-1">OPENAI_API_KEY</code> in de omgeving.
                      De topicale gaps hierboven werken zonder sleutel.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Nog niet berekend — draait wekelijks op vrijdagochtend voor
                    dossiers met GSC-data van de afgelopen 28 dagen.
                  </p>
                )
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>
                      Berekend op {contentGaps.computed_at
                        ? format(new Date(contentGaps.computed_at), 'd MMM yyyy HH:mm', { locale: nl })
                        : '—'}
                    </span>
                    <span>Model: {contentGaps.embedding_model ?? '—'}</span>
                    <span>Drempel: {((contentGaps.threshold ?? 0.8) * 100).toFixed(0)}% similariteit</span>
                    <span>{contentGaps.gaps?.length ?? 0} gaps / {contentGaps.total_analyzed ?? 0} geanalyseerd</span>
                  </div>
                  {(contentGaps.gaps ?? []).length === 0 ? (
                    <p className="text-sm text-green-600 dark:text-green-300">Geen content gaps gevonden.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left text-muted-foreground">
                            <th className="pb-2 pr-4 font-medium">Zoekvraag</th>
                            <th className="pb-2 pr-4 font-medium w-16 text-right">Impr.</th>
                            <th className="pb-2 pr-4 font-medium w-16 text-right">Pos.</th>
                            <th className="pb-2 pr-4 font-medium w-20 text-right">Sim.</th>
                            <th className="pb-2 font-medium">Beste match</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {(contentGaps.gaps as VectorContentGap[]).map((gap, i) => (
                            <tr key={i} className="hover:bg-muted/30">
                              <td className="py-2 pr-4 font-medium">{gap.query}</td>
                              <td className="py-2 pr-4 text-right tabular-nums">{gap.impressions.toLocaleString()}</td>
                              <td className="py-2 pr-4 text-right tabular-nums">{gap.position.toFixed(1)}</td>
                              <td className="py-2 pr-4 text-right tabular-nums">
                                <span className={cn(
                                  'text-xs font-medium px-1.5 py-0.5 rounded',
                                  gap.similarity < 0.5 && 'bg-red-100 dark:bg-red-950/30 text-red-700 dark:text-red-300',
                                  gap.similarity >= 0.5 && gap.similarity < 0.7 && 'bg-amber-100 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300',
                                  gap.similarity >= 0.7 && 'bg-slate-100 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300',
                                )}>
                                  {(gap.similarity * 100).toFixed(0)}%
                                </span>
                              </td>
                              <td className="py-2 text-muted-foreground text-xs truncate max-w-xs">
                                {gap.best_page_url
                                  ? <a href={gap.best_page_url} target="_blank" rel="noopener noreferrer"
                                      className="hover:underline text-primary">{gap.best_page_url}</a>
                                  : <span className="italic">Geen pagina gevonden</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* G11: SERP AI Overview monitoring */}
          <Card>
            <CardHeader>
              <CardTitle>AI Overview monitoring (SERP)</CardTitle>
              <CardDescription>
                Detecteert Google AI Overviews voor de top zoekvragen van dit dossier.
                Vereist <code className="bg-muted px-1 rounded">geo_aio_monitoring=true</code> + Playwright Chromium container.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!serpAio?.has_data ? (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Nog geen AI Overview data beschikbaar.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Activeer via AppSetting <code className="bg-muted px-1 rounded">geo_aio_monitoring=true</code> en start Celery-taak{' '}
                    <code className="bg-muted px-1 rounded">monitor_serp_aio({dossierId})</code>.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-2 pr-4 font-medium">Zoekvraag</th>
                        <th className="pb-2 pr-4 font-medium w-28 text-center">AI Overview</th>
                        <th className="pb-2 pr-4 font-medium w-24 text-center">Geciteerd</th>
                        <th className="pb-2 font-medium w-36">Bijgewerkt</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(serpAio.results as SerpAioResult[]).map((row, i) => (
                        <tr key={i} className="hover:bg-muted/30">
                          <td className="py-2 pr-4 font-medium truncate max-w-xs">{row.query}</td>
                          <td className="py-2 pr-4 text-center">
                            {row.ai_overview_present ? (
                              <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300">Ja</span>
                            ) : (
                              <span className="text-xs text-muted-foreground">Nee</span>
                            )}
                          </td>
                          <td className="py-2 pr-4 text-center">
                            {row.site_cited ? (
                              <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-950/30 text-green-700 dark:text-green-300">Ja</span>
                            ) : row.ai_overview_present ? (
                              <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-950/30 text-red-700 dark:text-red-300">Nee</span>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="py-2 text-xs text-muted-foreground">
                            {row.fetched_at
                              ? format(new Date(row.fetched_at), 'd MMM yyyy HH:mm', { locale: nl })
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* G13: Citation Prompts */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Citation Prompts</CardTitle>
                  <CardDescription>
                    Vragen die naar LLMs gestuurd worden om te testen of de site geciteerd wordt (G14).
                  </CardDescription>
                </div>
                <button
                  onClick={() => generatePrompts.mutate(undefined, {
                    onSuccess: (data) => {
                      if (data.added > 0) {
                        toast.success(`${data.added} prompt${data.added !== 1 ? 's' : ''} toegevoegd`, { description: data.reason })
                      } else {
                        toast.info('Geen nieuwe prompts', { description: data.reason })
                      }
                    },
                    onError: (err) => toast.error('Genereren mislukt', { description: err instanceof Error ? err.message : '' }),
                  })}
                  disabled={generatePrompts.isPending}
                  className="text-sm px-3 py-1.5 rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {generatePrompts.isPending ? 'Genereren…' : 'Genereer vanuit GSC'}
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Manual add */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPromptText}
                    onChange={e => setNewPromptText(e.target.value)}
                    placeholder="Voeg een eigen vraag toe…"
                    className="flex-1 text-sm border rounded px-3 py-1.5 bg-background"
                    onKeyDown={e => {
                      if (e.key === 'Enter' && newPromptText.trim()) {
                        createPrompt.mutate({ prompt_text: newPromptText.trim() }, {
                          onSuccess: () => setNewPromptText(''),
                        })
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      if (newPromptText.trim()) {
                        createPrompt.mutate({ prompt_text: newPromptText.trim() }, {
                          onSuccess: () => setNewPromptText(''),
                        })
                      }
                    }}
                    disabled={!newPromptText.trim() || createPrompt.isPending}
                    className="text-sm px-3 py-1.5 rounded border hover:bg-muted disabled:opacity-50"
                  >
                    Voeg toe
                  </button>
                </div>

                {/* Prompt list */}
                {!citationPrompts || citationPrompts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Nog geen citation prompts. Gebruik de knop om ze automatisch te genereren vanuit je GSC-zoekwoorden.
                  </p>
                ) : (
                  <div className="divide-y">
                    {(citationPrompts as CitationPrompt[]).map(p => (
                      <div key={p.id} className="py-2.5 flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={p.active}
                          onChange={e => updatePrompt.mutate({ promptId: p.id, data: { active: e.target.checked } })}
                          className="mt-1 shrink-0 cursor-pointer"
                        />
                        <div className="flex-1 min-w-0">
                          <p className={cn('text-sm', !p.active && 'line-through text-muted-foreground')}>
                            {p.prompt_text}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-muted-foreground">
                              {p.source === 'gsc_generated' ? 'GSC' : 'Handmatig'}
                            </span>
                            {p.intent_type && (
                              <span className="text-xs px-1 py-0 rounded bg-muted text-muted-foreground">
                                {p.intent_type}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => deletePrompt.mutate(p.id)}
                          className="shrink-0 text-xs text-muted-foreground hover:text-destructive"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Claude tab ── */}
      {activeTab === 'claude' && dossier && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Overzicht van alle Claude Code instructies voor dit dossier — kopieer en plak rechtstreeks in Claude Code.
          </p>

          {/* SEO */}
          {scans && scans.some(s => s.status === 'completed') && (
            <InstructionsPanel
              title="SEO instructies"
              prompt={generateSeoPrompt(dossier, scans, latestSnapshot, seoFindingsByType, avgScore)}
              loading={seoClaudeLoading}
            />
          )}

          {/* Security */}
          {securityScans && securityScans.some(s => s.status === 'completed') && (
            <InstructionsPanel
              title="Security instructies"
              prompt={generateSecurityPrompt(dossier, securityScans, secFindingsForPrompt)}
              loading={secClaudeLoading}
            />
          )}

          {/* GSC posities */}
          {positionBuckets && (
            <InstructionsPanel
              title="GSC positie-optimalisatie"
              prompt={buildGscPrompt(dossier, positionBuckets)}
            />
          )}

          {/* Indexeerbaarheid */}
          {indexReport && (
            <InstructionsPanel
              title="Indexeerbaarheid"
              prompt={buildIndexPrompt(dossier, indexReport)}
            />
          )}

          {/* Content audit instructies */}
          {allClaudeInstructions.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Bot className="h-5 w-5 text-primary" />
                  <CardTitle>Content audit instructies</CardTitle>
                </div>
                <CardDescription>Gegenereerd door LLM-analyse van pagina-content</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {(['hoog', 'gemiddeld', 'laag'] as const).map(priority => {
                    const forPriority = allClaudeInstructions.filter(i => i.priority === priority)
                    if (forPriority.length === 0) return null
                    const priorityLabel = { hoog: 'Hoge prioriteit', gemiddeld: 'Gemiddelde prioriteit', laag: 'Lage prioriteit' }[priority]
                    const headerCls = priority === 'hoog' ? 'text-red-700 dark:text-red-300' : priority === 'gemiddeld' ? 'text-amber-700 dark:text-amber-300' : 'text-slate-600 dark:text-slate-300'
                    return (
                      <div key={priority}>
                        <h3 className={`text-sm font-semibold mb-2 ${headerCls}`}>{priorityLabel}</h3>
                        <div className="space-y-2">
                          {forPriority.map((instr, i) => (
                            <ClaudeCodePromptCard key={i} instruction={instr} />
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Fallback when nothing is available yet */}
          {!scans?.some(s => s.status === 'completed') &&
           !securityScans?.some(s => s.status === 'completed') &&
           !positionBuckets && !indexReport &&
           allClaudeInstructions.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                Nog geen scans beschikbaar. Start scans via de andere tabbladen.
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ── WordPress tab ── */}
      {activeTab === 'wordpress' && (
        <div className="space-y-6">

          {/* Connection card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>WordPress verbinding</CardTitle>
                  <CardDescription>
                    Verbind via WP Application Password (WordPress 5.6+). Maak een Application Password aan via
                    Gebruikers → Profiel → Application Passwords in je WordPress dashboard.
                  </CardDescription>
                </div>
                {wpSite?.connected && (
                  <Badge variant="outline" className="border-green-500 text-green-600 dark:text-green-300">Verbonden</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Site URL</label>
                  <Input
                    placeholder="https://example.com"
                    value={wpForm.site_url || (wpSite?.connected ? wpSite.site_url ?? '' : '')}
                    onChange={e => setWpForm(f => ({ ...f, site_url: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">WordPress gebruiker</label>
                  <Input
                    placeholder="admin"
                    value={wpForm.api_user || (wpSite?.connected ? wpSite.api_user ?? '' : '')}
                    onChange={e => setWpForm(f => ({ ...f, api_user: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Application Password</label>
                  <Input
                    type="password"
                    placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                    value={wpForm.api_password}
                    onChange={e => setWpForm(f => ({ ...f, api_password: e.target.value }))}
                  />
                </div>
              </div>

              {wpTestResult && (
                <div className={cn(
                  'rounded-md px-3 py-2 text-sm',
                  wpTestResult.ok ? 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300' : 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300'
                )}>
                  {wpTestResult.ok
                    ? `✓ Verbinding OK — "${wpTestResult.site_info?.name}" (${wpTestResult.site_info?.wp_version || 'onbekende versie'})`
                    : `✗ Verbinding mislukt: ${wpTestResult.error}`
                  }
                </div>
              )}

              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={testWpConnection.isPending}
                  onClick={async () => {
                    const url = wpForm.site_url || wpSite?.site_url || ''
                    const user = wpForm.api_user || wpSite?.api_user || ''
                    const pw = wpForm.api_password
                    if (!url || !user || !pw) { toast.error('Vul alle velden in'); return }
                    const result = await testWpConnection.mutateAsync({ site_url: url, api_user: user, api_password: pw })
                    setWpTestResult(result)
                  }}
                >
                  {testWpConnection.isPending ? <Spinner className="mr-2 h-4 w-4" /> : null}
                  Test verbinding
                </Button>
                <Button
                  size="sm"
                  disabled={saveWpSite.isPending}
                  onClick={() => {
                    const url = wpForm.site_url || wpSite?.site_url || ''
                    const user = wpForm.api_user || wpSite?.api_user || ''
                    const pw = wpForm.api_password
                    if (!url || !user || !pw) { toast.error('Vul alle velden in'); return }
                    saveWpSite.mutate(
                      { site_url: url, api_user: user, api_password: pw },
                      {
                        onSuccess: () => { toast.success('WordPress verbinding opgeslagen'); setWpForm({ site_url: '', api_user: '', api_password: '', enabled: true }); setWpTestResult(null) },
                        onError: (e) => toast.error(`Opslaan mislukt: ${e.message}`),
                      }
                    )
                  }}
                >
                  Opslaan
                </Button>
                {wpSite?.connected && (
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={deleteWpSite.isPending}
                    onClick={() => {
                      if (!confirm('Verbinding verwijderen?')) return
                      deleteWpSite.mutate(undefined, {
                        onSuccess: () => { toast.success('WordPress verbinding verwijderd'); setWpTestResult(null) },
                        onError: (e) => toast.error(`Verwijderen mislukt: ${e.message}`),
                      })
                    }}
                  >
                    Verbinding verwijderen
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Scan results */}
          {wpSite?.connected && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle>WordPress scan</CardTitle>
                    <CardDescription>
                      {wpScanResults?.last_scanned_at
                        ? `Laatste scan: ${format(new Date(wpScanResults.last_scanned_at), 'd MMM yyyy HH:mm', { locale: nl })}`
                        : 'Nog geen scan uitgevoerd'}
                    </CardDescription>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={triggerWpScan.isPending}
                    onClick={() =>
                      triggerWpScan.mutate(undefined, {
                        onSuccess: () => toast.success('WordPress scan gestart'),
                        onError: (e) => toast.error(`Scanfout: ${e.message}`),
                      })
                    }
                  >
                    <Play className="h-4 w-4 mr-1" />
                    Scan starten
                  </Button>
                </div>
              </CardHeader>
              {wpScanResults?.has_data && (
                <CardContent className="space-y-6">

                  {/* Issues summary */}
                  {wpScanResults.issues && (
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className={cn(
                        'rounded-lg border p-3',
                        (wpScanResults.issues.plugins_with_updates?.length ?? 0) > 0 ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30' : 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30'
                      )}>
                        <p className="text-xs font-medium text-muted-foreground">Plugin updates</p>
                        <p className={cn(
                          'text-2xl font-bold mt-1',
                          (wpScanResults.issues.plugins_with_updates?.length ?? 0) > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-green-700 dark:text-green-300'
                        )}>
                          {wpScanResults.issues.plugins_with_updates?.length ?? 0}
                        </p>
                        <p className="text-xs text-muted-foreground">van {wpScanResults.issues.plugin_count ?? 0} plugins</p>
                      </div>
                      <div className={cn(
                        'rounded-lg border p-3',
                        (wpScanResults.issues.orphan_pages?.length ?? 0) > 0 ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30' : 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30'
                      )}>
                        <p className="text-xs font-medium text-muted-foreground">Orphan pagina's</p>
                        <p className={cn(
                          'text-2xl font-bold mt-1',
                          (wpScanResults.issues.orphan_pages?.length ?? 0) > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-green-700 dark:text-green-300'
                        )}>
                          {wpScanResults.issues.orphan_pages?.length ?? 0}
                        </p>
                        <p className="text-xs text-muted-foreground">geen inkomende links</p>
                      </div>
                      <div className={cn(
                        'rounded-lg border p-3',
                        (Object.keys(wpScanResults.issues.focus_kw_conflicts ?? {}).length) > 0 ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30' : 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30'
                      )}>
                        <p className="text-xs font-medium text-muted-foreground">Focus-kw conflicten</p>
                        <p className={cn(
                          'text-2xl font-bold mt-1',
                          (Object.keys(wpScanResults.issues.focus_kw_conflicts ?? {}).length) > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-green-700 dark:text-green-300'
                        )}>
                          {Object.keys(wpScanResults.issues.focus_kw_conflicts ?? {}).length}
                        </p>
                        <p className="text-xs text-muted-foreground">zoekwoorden op meerdere pagina's</p>
                      </div>
                    </div>
                  )}

                  {/* Active theme */}
                  {wpScanResults.active_theme && (
                    <div>
                      <h3 className="text-sm font-medium mb-2">Actief thema</h3>
                      <div className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm">
                        <span className="font-medium">{wpScanResults.active_theme.name}</span>
                        <span className="text-muted-foreground">v{wpScanResults.active_theme.version}</span>
                        {wpScanResults.active_theme.update_available && (
                          <Badge variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-300 text-xs">Update beschikbaar</Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Plugin list */}
                  {wpScanResults.plugins && wpScanResults.plugins.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium mb-2">
                        Plugins ({wpScanResults.issues?.active_plugin_count ?? wpScanResults.plugins.filter(p => p.active).length} actief
                        {(wpScanResults.issues?.plugins_with_updates?.length ?? 0) > 0 && (
                          <span className="text-amber-600 dark:text-amber-300 ml-1">· {wpScanResults.issues?.plugins_with_updates?.length} updates</span>
                        )})
                      </h3>
                      <div className="divide-y rounded-md border text-sm">
                        {wpScanResults.plugins.map((plugin: WordPressPlugin) => (
                          <div key={plugin.plugin_file} className={cn('flex items-center gap-3 px-3 py-2', !plugin.active && 'opacity-50')}>
                            <span className={cn('h-2 w-2 rounded-full shrink-0', plugin.active ? 'bg-green-500' : 'bg-slate-300')} />
                            <span className="flex-1 font-medium truncate">{plugin.name}</span>
                            <span className="text-muted-foreground shrink-0">v{plugin.version}</span>
                            {plugin.update_available && (
                              <Badge variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-300 text-xs shrink-0">Update</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Orphan pages */}
                  {(wpScanResults.issues?.orphan_pages?.length ?? 0) > 0 && (
                    <div>
                      <h3 className="text-sm font-medium mb-2">Orphan pagina's</h3>
                      <div className="divide-y rounded-md border text-sm">
                        {wpScanResults.issues!.orphan_pages.map((url: string) => (
                          <div key={url} className="flex items-center gap-2 px-3 py-2">
                            <Link2Off className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                            <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-300 hover:underline truncate">
                              {url}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Focus keyword conflicts */}
                  {Object.keys(wpScanResults.issues?.focus_kw_conflicts ?? {}).length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium mb-2">Focus-zoekwoord conflicten</h3>
                      <div className="space-y-2">
                        {Object.entries(wpScanResults.issues!.focus_kw_conflicts).map(([kw, urls]) => (
                          <div key={kw} className="rounded-md border px-3 py-2 text-sm">
                            <p className="font-medium mb-1">&ldquo;{kw}&rdquo;</p>
                            <ul className="space-y-0.5 text-muted-foreground text-xs">
                              {(urls as string[]).map((u: string) => (
                                <li key={u}>
                                  <a href={u} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-300 hover:underline">{u}</a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Content list with orphan indicator */}
                  {wpScanResults.content && wpScanResults.content.length > 0 && (
                    <details>
                      <summary className="text-sm font-medium cursor-pointer hover:text-muted-foreground">
                        Alle content ({wpScanResults.content.length} items)
                      </summary>
                      <div className="mt-2 divide-y rounded-md border text-sm max-h-80 overflow-y-auto">
                        {wpScanResults.content.map((item: WordPressContentItem) => (
                          <div key={item.id} className={cn('flex items-center gap-3 px-3 py-2', item.is_orphan && 'bg-amber-50 dark:bg-amber-950/30/50')}>
                            <span className="text-xs text-muted-foreground w-12 shrink-0">{item.type}</span>
                            <span className="flex-1 truncate">{item.title || item.slug}</span>
                            {item.focus_kw && (
                              <span className="text-xs text-muted-foreground shrink-0">🎯 {item.focus_kw}</span>
                            )}
                            {item.is_orphan && (
                              <span className="text-xs text-amber-600 dark:text-amber-300 shrink-0">orphan</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}

                </CardContent>
              )}
            </Card>
          )}
        </div>
      )}

    </div>
  )
}

export default function DossierDetail() {
  return (
    <DossierErrorBoundary>
      <DossierDetailInner />
    </DossierErrorBoundary>
  )
}
